import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Spin,
  Button,
  Divider,
  Select,
  Tag,
  InputNumber,
  Collapse,
  Checkbox,
  Table,
  Input,
  Radio,
  Tooltip,
  message,
  Alert,
  Popconfirm,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faElevator,
  faFingerprint,
  faTriangleExclamation,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
// COMPONENTS
import FormInputText from "../../components/form/FormInputText";
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
import GlobalAlert2 from "../../home/GlobalAlert2";
// REDUX
import {
  useLazyGetBankQuery,
  useLazyGetBanksByServerQuery,
  usePutBankMutation,
} from "../../../redux/banks/bankAPI";
import { useLazyGetChannelQuery } from "../../../redux/channels/channelAPI";
import {
  usePostLiftMutation,
  usePutLiftMutation,
  useDeleteLiftMutation,
} from "../../../redux/lifts/liftAPI";
import {
  useLazyGetDraftQuery,
  useUpsertDraftMutation,
  useDeleteDraftMutation,
} from "../../../redux/wizardDrafts/wizardDraftsAPI";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
// CONTEXT
import { useWizard } from "../WizardContext";
import { adapterProtocolName } from "../../components/adapters/adapterMatrixInputs";
import {
  OPC_PATH_PROTOCOLS,
  OPC_VENDOR_LIFT_FIELDS,
  OPC_VENDOR_LIFT_LABELS,
  OpcVendorLiftField,
} from "../../../constants/opcVendorPaths";
import { WizardStep } from "../utils/wizardInvalidation";
import { resolveStandaloneBankScope } from "../utils/standaloneBankScope";
import { useWizardStepEntry } from "../utils/useWizardStepEntry";
import { describeError } from "../utils/describeError";
// STORE
import { RootState } from "../../../app/store";
// ENTITY LINK
import WizardEntityLink from "../WizardEntityLink";
import WizardReadOnlyId from "../WizardReadOnlyId";
import WizardIdHoverTag from "../WizardIdHoverTag";
import WizardExportButton from "../components/WizardExportButton";
import { WizardOpGate, useCurrentWizardStepAccess } from "../WizardStepGate";
// OFFSET MATRIX
import BankOffsetMatrix, { extractMappedMessageTypes } from "./BankOffsetMatrix";
import type { MappedMessageType } from "./BankOffsetMatrix";
// INTERFACES
import { FormularyType } from "../../../interfaces/Wizard.interface";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import { formatUuidT } from "../../../flatbuffers/display";
import {
  assignSequentialPositions,
  hasDegeneratePositions,
} from "../../../utils/bankFloorPositions";
import {
  extractBankFloorOpenings,
  floorIndexOrZero,
  readDoorFlags,
} from "../../../utils/bankFloorOpenings";
import { devWarn } from "../../../utils/devLog";
import { extractEnvelopeWithHash } from "../../../flatbuffers/envelope";
import { responseSchemaRefusal } from "../../../flatbuffers/api/apiVerdicts";
import { getSchemaHandler } from "../../../flatbuffers/api/schemaRegistry";
import {
  noteServerSchema,
  responseSchema,
  schemaRequestHeaders,
} from "../../../flatbuffers/api/serverSchemas";

/** Fetch a URL and decode FlatBuffer or JSON response transparently.
 *  Returns the innermost data object (unwraps { status, data } wrappers).
 *  Throws when the server wrote the body with an API schema whose type this
 *  bundle cannot read unchanged (see `apiVerdicts.ts`). */
async function fetchAndDecode(url: string, token: string): Promise<any> {
  const baseUrl = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
  const resp = await fetch(url, {
    headers: {
      authorization: token,
      Accept: "application/flatbuffers",
      ...schemaRequestHeaders(baseUrl),
    },
  });
  if (!resp.ok) return null;
  const writtenWith = responseSchema(resp);
  noteServerSchema(baseUrl, writtenWith);
  const ct = resp.headers.get("Content-Type") ?? "";
  if (ct.includes("application/flatbuffers")) {
    const buf = new Uint8Array(await resp.arrayBuffer());
    const env = extractEnvelopeWithHash(buf);
    if (env) {
      const handler = getSchemaHandler(env.schemaHash);
      if (handler) {
        const refusal = await responseSchemaRefusal(baseUrl, writtenWith, handler.typeName, token);
        if (refusal !== null) {
          throw new Error(refusal);
        }
        handler.verify(env.payload);
        const result: any = handler.unpack(env.payload);
        // Unwrap { status, data } wrapper if present
        return result?.data ?? result;
      }
    }
    return null;
  }
  const json = await resp.json();
  return json?.data ?? json;
}

/** Types that have floors / stops / rear doors. */
const FLOOR_TYPES = new Set<string>([LiftType[LiftType.ELEVATOR], LiftType[LiftType.BOT]]);

/**
 * Given an array of floor name strings, detects the first two non-empty numeric
 * names, computes the arithmetic stride, and fills any empty slots.
 * Returns null when the pattern cannot be determined.
 */
function deriveFloorSequence(names: string[], totalFloors: number): string[] | null {
  type FilledEntry = { index: number; value: number } | null; // null signals invalid input
  const filled = names.reduce<FilledEntry[]>((acc, name, i) => {
    if (acc.length >= 2 || acc.includes(null)) return acc;
    const trimmed = name?.trim();
    if (!trimmed) return acc;
    const num = parseInt(trimmed, 10);
    if (Number.isNaN(num)) return [...acc, null];
    return [...acc, { index: i, value: num }];
  }, []);

  if (filled.includes(null) || filled.length < 2) return null;

  const [first, second] = filled as [
    { index: number; value: number },
    { index: number; value: number },
  ];
  const indexDiff = second.index - first.index;
  if (indexDiff === 0) return null;
  const stridePerIndex = (second.value - first.value) / indexDiff;
  if (!Number.isInteger(stridePerIndex)) return null;

  const baseValue = first.value - first.index * stridePerIndex;

  return Array.from({ length: totalFloors }, (_, i) => {
    const existing = names[i]?.trim();
    return existing || String(baseValue + i * stridePerIndex);
  });
}

interface BankOption {
  id: string;
  name: string;
  type: string;
  number_of_lifts: number;
  floor_count: number;
  stops: number;
  floor_below_lobby: number;
  start_floor_no: number;
  rear_doors: boolean;
  bank_floors: any[];
  lifts: any[];
  rawData: any;
}

interface FloorOpening {
  floor_no: number;
  floor_name: string;
  front_door: boolean;
  rear_door: boolean;
  bank_floor_id: string;
  lift_floor_id: string;
}

interface LiftEntry {
  name: string;
  position: number;
  /**
   * Explicit per-lift car number (independent of `position`/bank ordering).
   * Optional passthrough field — null means unset (mirrors Bank.logical_port,
   * `StepBanks.tsx:1136`). Sent as a plain REST field (`models.LiftRequest.CarNum`
   * on LiftCloud) alongside `position` in the lift create/update body — same
   * pattern as `position`, NOT packed into any FlatBuffer. `Archive.Lift` (the
   * wizard/config Lift schema) has no car_num field, and
   * `FrontEndConfig.Lift.car_num` is a DIFFERENT, already-load-bearing concept
   * (LN1's unit index) — do not conflate the two. Consumed by LV2's
   * `LiftCarNum` addressing source (`AdapterCdoLobbyVision2.interface.ts`) at
   * CDO-expansion time via the REST-authored value on the Lift record.
   */
  car_num: number | null;
  /**
   * OPC vendor node-path segment for this car, and its OPC DA subscription
   * group. Only meaningful when the bank's adapter is OPC DA or OPC UA; the
   * inputs are hidden otherwise.
   *
   * Distinct from `name`: `name` is the LN2 display label an operator chose,
   * these are the tokens the vendor's OPC server exposes. A bank named
   * "Service Elevators" can address cars called "SE1"/"SE2".
   */
  vendor_lift_path?: string | null;
  vendor_group_name?: string | null;
  floor_count: number;
  floor_below_lobby: number;
  openings: FloorOpening[];
  saved: boolean;
  verified: boolean;
  liftId: string;
}

interface BankFloorEntry {
  id: string;
  floor_no: number;
  name: string;
  floor_below_lobby: boolean;
  floor_index: number;
  position: number;
}

/** Converts an app-level LiftType string (e.g. "ELEVATOR") to the FlatBuffer numeric enum. */
function toLiftType(liftType: string): LiftType {
  const map: Record<string, LiftType> = {
    ELEVATOR: LiftType.ELEVATOR,
    ESCALATOR: LiftType.ESCALATOR,
    MOVINGWALK: LiftType.MOVINGWALK,
    BOT: LiftType.BOT,
    GPIO: LiftType.GPIO,
  };
  return map[liftType.toUpperCase()] ?? LiftType.ELEVATOR;
}

// Reorder floors so below-lobby are deepest-first (B5,B4,...,B1) then above-lobby ascending
function reorderBelowLobbyFirst<T>(items: T[], isBelowLobby: (item: T) => boolean): T[] {
  const below = items.filter(isBelowLobby);
  const above = items.filter((item) => !isBelowLobby(item));
  below.reverse();
  return [...below, ...above];
}

export function buildDefaultOpenings(
  floorCount: number,
  floorBelowLobby: number,
  startFloorNo: number,
  bankFloors?: any[],
): FloorOpening[] {
  if (bankFloors && bankFloors.length > 0) {
    // Decorate-sort-undecorate: a plain comparator calls floorIndexOrZero twice
    // per comparison (more than once per row for anything past 2 floors);
    // computing it once per row up front avoids that.
    const sorted = bankFloors
      .map((bf) => ({ bf, floorIndex: floorIndexOrZero(bf) }))
      .sort((a, b) => a.floorIndex - b.floorIndex)
      .map(({ bf }) => bf);
    // Reuse the shared extractor for door capability rather than re-deriving
    // the camelCase/snake_case-with-fallback coercion by hand — it already
    // gets the numeric-flag edge case right (see its own doc comment) and is
    // the standardized read path for this exact data elsewhere in the
    // codebase (src/components/scheduledActions/useBankFloorOpenings.ts).
    const doorById = new Map(
      extractBankFloorOpenings({ bank_floors: bankFloors }).map((f) => [f.id, f]),
    );
    const mapped = sorted.map((bf: any) => {
      const fno = bf.floorNo ?? bf.floor_no ?? 0;
      const belowLobby = bf.floorBelowLobby ?? bf.floor_below_lobby ?? false;
      const bfId = typeof bf.id === "object" ? formatUuidT(bf.id) || "" : bf.id || "";
      const door = doorById.get(bfId);
      return {
        floor_no: fno,
        floor_name: String(bf.name || (belowLobby ? `B${fno}` : `${fno}`)),
        // A new lift's default openings must reflect THIS floor's own door
        // capability, not a blanket true/false for every floor — a rear-only
        // floor (front_door=false, rear_door=true) otherwise arrives with an
        // unusable checked-but-disabled front box and an unchecked rear box,
        // which is the exact "some floors only having the rear door open"
        // case LIFT-523 reported. Falls back to true/false (extractBankFloorOpenings'
        // own defaults) only when the bank_floor itself doesn't say.
        front_door: door?.front ?? true,
        rear_door: door?.rear ?? false,
        bank_floor_id: bfId,
        lift_floor_id: "",
        isBelowLobby: belowLobby,
      };
    });
    const reordered = reorderBelowLobbyFirst(mapped, (o) => o.isBelowLobby);
    return reordered.map(({ isBelowLobby, ...rest }) => rest);
  }
  const openings: FloorOpening[] = [];
  // Below-lobby: deepest first (B5, B4, ..., B1)
  for (let i = floorBelowLobby; i >= 1; i -= 1) {
    openings.push({
      floor_no: i,
      floor_name: `B${i}`,
      front_door: true,
      rear_door: false,
      bank_floor_id: "",
      lift_floor_id: "",
    });
  }
  // Above-lobby: ascending
  for (let i = 0; i < floorCount - floorBelowLobby; i += 1) {
    const floorNo = startFloorNo + i;
    openings.push({
      floor_no: floorNo,
      floor_name: `${floorNo}`,
      front_door: true,
      rear_door: false,
      bank_floor_id: "",
      lift_floor_id: "",
    });
  }
  return openings;
}

/**
 * Validates a lift name and returns an error string if invalid, or null if valid.
 * A lift name is valid when it is non-empty (any non-whitespace content is accepted,
 * including names that start with digits such as "1" or "1A").
 */
export function validateLiftName(name: string): string | null {
  if (!name.trim()) return "name required";
  return null;
}

/** How many unnamed floors an error names before it summarises the rest. */
const MAX_NAMED_FLOORS_IN_ERROR = 6;

/**
 * Validates the bank's floor names, returning an error string or null.
 *
 * An unnamed floor is an error HERE and a warning at runtime, and the asymmetry
 * is deliberate. Monitoring falls back to the floor number so a button still
 * renders something an operator mid-call can press; this step is where the
 * omission can actually be fixed, and letting it save is how a bank reaches
 * monitoring with buttons that read 1-12 while the plates beside the doors read
 * L, M, 2, 3.
 *
 * Names the offending floors by their floor number rather than counting them,
 * because "3 floors are unnamed" on a twelve-floor bank sends the user hunting.
 * Capped so a bank whose names were never entered at all reports a readable
 * line instead of every floor it has.
 */
export function validateFloorNames(entries: { floor_no: number; name: string }[]): string | null {
  const unnamed = entries.filter((e) => !e.name?.trim());
  if (unnamed.length === 0) return null;

  if (unnamed.length === entries.length) {
    return `no floor is named — name all ${entries.length} floors, or use Auto-fill Names`;
  }

  const shown = unnamed.slice(0, MAX_NAMED_FLOORS_IN_ERROR).map((e) => e.floor_no);
  const suffix =
    unnamed.length > MAX_NAMED_FLOORS_IN_ERROR
      ? `, +${unnamed.length - MAX_NAMED_FLOORS_IN_ERROR} more`
      : "";
  return `floors ${shown.join(", ")}${suffix} have no name`;
}

/**
 * Add the optional `car_num` field to a lift request body, mirroring the
 * Bank.logical_port passthrough convention (`StepBanks.tsx:1136/1390`):
 * only included when set, so unconfigured lifts don't send a stray
 * `car_num: null`. Extracted as a pure function so the conditional-add
 * logic is unit-testable without rendering the wizard step.
 */
/**
 * Add the OPC vendor path segments to a lift request body.
 *
 * Two regimes, selected by `authoritative` — the same split the bank editors use
 * through `Archive.Bank.vendor_paths_provided`, now mirrored on `Archive.Lift`:
 *
 * - Authoritative (this step is rendering the inputs, i.e. the bank is on an OPC
 *   dialect): every segment goes on every save, trimmed, `""` for a blank one,
 *   plus the presence flag. The server then writes each column to its value or to
 *   NULL — the only way to delete a wrong path, since FlatBuffers cannot express a
 *   null string and an empty segment is otherwise byte-identical to an absent one.
 * - Not authoritative (every other protocol, which never renders these inputs):
 *   only a non-blank segment is sent and no flag at all, so the server's guarded
 *   write leaves any stored columns untouched. Sending `""` here would be
 *   indistinguishable from a deliberately configured empty segment, and an empty
 *   segment expands to a node path that matches nothing on the OPC server while
 *   raising no error.
 *
 * Pure so the conditional-add logic is unit-testable without rendering the step.
 */
export function withVendorPaths(
  body: Record<string, unknown>,
  entry: Pick<LiftEntry, "vendor_lift_path" | "vendor_group_name">,
  authoritative = false,
): Record<string, unknown> {
  const next = { ...body };

  OPC_VENDOR_LIFT_FIELDS.forEach((field: OpcVendorLiftField) => {
    const trimmed = (entry[field] ?? "").trim();
    if (trimmed !== "" || authoritative) {
      next[field] = trimmed;
    }
  });

  if (authoritative) {
    next.vendor_paths_provided = true;
  }

  return next;
}

export function withCarNum(
  body: Record<string, unknown>,
  carNum: number | null,
): Record<string, unknown> {
  if (carNum == null) return body;
  return { ...body, car_num: carNum };
}

/**
 * Add the optional `event_reporting_address` field to a bank update body,
 * mirroring this same convention (car_num above; logical_port inline at this
 * file's saveBankFloorNames call site): only included when set, so a bank
 * whose MCE address isn't configured doesn't send a stray
 * `event_reporting_address: null`. Extracted as a pure function —
 * saveBankFloorNames isn't exported and this component has no render
 * harness — so the conditional-add logic is unit-testable without either.
 *
 * The distinction this guards is not cosmetic: LiftCloud's
 * applyOptionalBankUpdateFields deliberately writes an explicit NULL to
 * event_reporting_address whenever the key is absent from the request (it's
 * one of the two fields meant to be clearable that way, unlike most other
 * optional Bank fields). Before this helper existed, saveBankFloorNames
 * built its logical_port passthrough but never carried
 * event_reporting_address across at all — so every floor-name save silently
 * cleared a bank's configured MCE address. See
 * project_bank_logicalport_eventreportingaddress_clobber for the full trace.
 *
 * Treats an empty string the same as null/undefined — `??` alone doesn't
 * fall through on `""`, so a caller whose `raw` carries an empty-but-present
 * address would otherwise add the key with a blank value, contradicting
 * "only included when set" above (requestSerializers.ts's own
 * event_reporting_address handling normalizes "" the same way for the same
 * reason).
 */
export function withEventReportingAddress(
  body: Record<string, unknown>,
  eventReportingAddress: string | null,
): Record<string, unknown> {
  if (eventReportingAddress == null || eventReportingAddress === "") return body;
  return { ...body, event_reporting_address: eventReportingAddress };
}

function countStops(openings: FloorOpening[]): number {
  return openings.filter((o) => o.front_door || o.rear_door).length;
}

/**
 * Exported only so a regression test can pin that this stays byte-for-byte
 * unaffected by the countActiveFloors fix below — not otherwise consumed
 * outside this file. computeFloorCounts duplicates countActiveFloors' old,
 * unfixed counting shape (it never resolves whether o.bank_floor_id still
 * matches a live floor either) and is deliberately left that way here: see
 * countActiveFloors' own doc comment for why the two are no longer
 * interchangeable.
 */
export function computeFloorCounts(
  openings: FloorOpening[],
  bankFloors: BankFloorEntry[],
): { floor_count: number; floor_below_lobby: number } {
  let floorCount = 0;
  let floorBelowLobby = 0;
  openings.forEach((o) => {
    if (o.front_door || o.rear_door) {
      floorCount += 1;
      const bf = bankFloors.find((b) => b.id === o.bank_floor_id);
      const isBelowLobby = bf ? bf.floor_below_lobby : o.floor_name.startsWith("B");
      if (isBelowLobby) floorBelowLobby += 1;
    }
  });
  return { floor_count: floorCount, floor_below_lobby: floorBelowLobby };
}

/**
 * Resolves a single opening's bank_floor_id against the live bankFloors set:
 * either it's stale (a non-empty id with no match, once bankFloors is known
 * — exclude it entirely) or it isn't, in which case the matched
 * BankFloorEntry (if any) is returned for the caller's own per-floor use.
 * Encodes exactly the exception rules countActiveFloors' doc comment below
 * spells out — pulled into its own step, not inlined into that function's
 * loop, so a future fix to computeFloorCounts (see that function's own doc
 * comment) has a tested seam to call instead of re-deriving this a third
 * time (it's already duplicated once, in liveOpeningsFor's own id check
 * above). Exported so other per-opening staleness UI (e.g. flagging which
 * specific row is dangling) can reuse this instead of a fourth copy.
 */
export function resolveLiveBankFloor(
  o: FloorOpening,
  bankFloors: BankFloorEntry[],
): { stale: boolean; bankFloor: BankFloorEntry | undefined } {
  const hasKnownFloors = bankFloors.length > 0;
  const bankFloor = bankFloors.find((b) => b.id === o.bank_floor_id);
  const stale = hasKnownFloors && Boolean(o.bank_floor_id) && !bankFloor;
  return { stale, bankFloor };
}

/**
 * Counts openings that are "active" (a door is checked) for the >=2-floor
 * validation gate (allLiftsValid / liftValidationErrors) and the per-lift
 * header render — NOT for computeFloorCounts above, which duplicates this
 * shape but is intentionally not updated to match (no call relationship
 * between the two — verified by a full-file grep for "countActiveFloors(",
 * which returns only this definition and three real, unrelated call sites).
 *
 * An opening is skipped when its bank_floor_id is non-empty but resolves to
 * no entry in bankFloors — a dangling reference to a floor deleted on the
 * Banks step since this opening was populated. Counting those was the bug
 * (LIFT-523, fourth follow-up — see this file's test header comment): the
 * gate passed lifts whose "active" floors partly no longer existed, then
 * liveOpeningsFor (which DOES resolve against live floors) silently saved
 * fewer floors than the gate had just required.
 *
 * Two deliberate exceptions, each covering a different regime — conflating
 * their justifications is what would make one of them look removable:
 * - bankFloors is empty (hasKnownFloors false): nothing CAN resolve, so
 *   nothing is judged stale. Covers not-yet-hydrated state and a genuinely
 *   zero-floor bank — the same "an empty known-set is inconclusive, not
 *   authoritative" exception reconcileBankFloorCapabilities/liveOpeningsFor
 *   above already document. This alone fully covers the zero-floor-bank
 *   case; the exception below is never reached there.
 * - bank_floor_id is empty (only reachable once bankFloors is non-empty,
 *   since the exception above already owns the empty-bankFloors regime): an
 *   id never assigned is unassigned, not stale — a synthetic opening from
 *   buildDefaultOpenings' no-bank-floors fallback, not yet backed by a real
 *   floor. Falsy-checked so a stray null/undefined from looser API data is
 *   also treated as "no id" despite the string type.
 *
 * KNOWN GAP, pre-existing and deliberately NOT fixed here: once a bank that
 * had zero floors when an opening was created later gains real floors, that
 * opening's still-empty bank_floor_id is counted here but DROPPED by
 * liveOpeningsFor at save time (liveBankFloorIds.has("") is always false
 * once that set is non-empty) — the same validate-vs-save divergence this
 * fix closes, reappearing for the empty-id case specifically. Both halves of
 * this already ship today, unchanged by this fix either direction; closing
 * it needs a bank_floor_id backfill in liveOpeningsFor or reconcileLiftIds,
 * out of scope here and tracked as a separate follow-up.
 */
export function countActiveFloors(
  openings: FloorOpening[],
  bankFloors: BankFloorEntry[],
): { activeTotal: number; activeBelowLobby: number } {
  let activeTotal = 0;
  let activeBelowLobby = 0;
  openings.forEach((o) => {
    // Door predicate copied verbatim from computeFloorCounts above — not a
    // call to canOpenDoor, which answers a capability question ("can this
    // door be opened at all"), not this function's activity question ("is
    // it currently checked").
    if (!(o.front_door || o.rear_door)) return;
    const { stale, bankFloor } = resolveLiveBankFloor(o, bankFloors);
    if (stale) return;
    activeTotal += 1;
    const isBelowLobby = bankFloor ? bankFloor.floor_below_lobby : o.floor_name.startsWith("B");
    if (isBelowLobby) activeBelowLobby += 1;
  });
  return { activeTotal, activeBelowLobby };
}

/**
 * Builds the "at least 2 active floors" validation message for a lift,
 * naming any CHECKED opening(s) whose bank_floor_id no longer resolves
 * against a live bank floor (see resolveLiveBankFloor's doc comment) — these
 * are exactly the openings countActiveFloors silently drops from the count,
 * so naming them tells the operator which specific checkbox to distrust
 * instead of leaving them to guess among every row in the table. An
 * unchecked stale opening is not named here — it was never counted either
 * way, so it isn't part of "why this lift fails" — even though it does get
 * its own always-on row indicator (see the Floor Openings table's Floor
 * column render in the JSX below) so the operator sees it before checking
 * it, not only after.
 *
 * Falls back to the pre-existing plain message, byte-for-byte, when the
 * failure has no stale opening involved: a lift that has simply not had
 * enough boxes checked yet keeps the exact message it always has.
 *
 * There is no self-service recovery through this message or the row
 * indicator — confirmed elsewhere that toggling a checkbox never rewrites
 * bank_floor_id, so a stale opening cannot rebind itself by being
 * unchecked and rechecked. Both surfaces are visibility only; the message
 * says "check a different floor" rather than implying this one can be
 * fixed in place.
 *
 * Pure and exported so this is unit-testable without rendering the wizard
 * step, matching every other predicate in this file.
 */
export function activeFloorsValidationMessage(
  openings: FloorOpening[],
  bankFloors: BankFloorEntry[],
): string {
  const plain = "must have at least 2 active floors";
  const staleCheckedNames = openings
    .filter((o) => (o.front_door || o.rear_door) && resolveLiveBankFloor(o, bankFloors).stale)
    .map((o) => `"${o.floor_name}"`);
  if (staleCheckedNames.length === 0) return plain;
  const plural = staleCheckedNames.length > 1;
  return `${plain} (checked floor${plural ? "s" : ""} no longer on this bank: ${staleCheckedNames.join(", ")} — check a different floor instead)`;
}

/** Normalize a raw ID field (UUID object or string) to a string. */
function extractId(id: any): string {
  return typeof id === "object" && id !== null ? formatUuidT(id) || "" : id || "";
}

export function loadExistingLifts(bank: BankOption): LiftEntry[] {
  return [...bank.lifts]
    .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
    .map((lift: any) => {
      // API may return camelCase (liftFloors) or snake_case (lift_floors)
      const liftFloors: any[] = lift.liftFloors ?? lift.lift_floors ?? [];
      // Decorate-sort-undecorate: resolve each row's floorIndex once, not
      // twice per comparison the sort makes.
      const sorted = liftFloors
        .map((lf: any) => {
          const lfId = extractId(lf.bankFloorId ?? lf.bank_floor_id);
          const bf = bank.bank_floors.find((b: any) => extractId(b.id) === lfId);
          return { lf, floorIndex: floorIndexOrZero(bf) };
        })
        .sort((a, b) => a.floorIndex - b.floorIndex)
        .map(({ lf }) => lf);

      const mapped = sorted.map((lf: any) => {
        const lfBankFloorId = extractId(lf.bankFloorId ?? lf.bank_floor_id);
        const bankFloor = bank.bank_floors.find((bf: any) => extractId(bf.id) === lfBankFloorId);
        const fno = bankFloor?.floorNo ?? bankFloor?.floor_no ?? 0;
        const isBelow = bankFloor?.floorBelowLobby ?? bankFloor?.floor_below_lobby ?? false;
        const floorName = bankFloor?.name || (isBelow ? `B${fno}` : `${fno}`);
        // Read through the shared reader, not a raw `??` chain: this feeds
        // `o.front_door`/`o.rear_door` below, which the save read-back
        // verification (in handleSaveAll) compares with a STRICT `!==`
        // against what that same verification reads back off the server —
        // also migrated to this reader, in the same change. Migrating one
        // site without the other would make a raw numeric flag (e.g.
        // `frontDoor: 1`) compare unequal to its own coerced boolean and
        // report a save that succeeded as "could not be verified".
        const doors = readDoorFlags(lf, true, false);
        return {
          floor_no: fno,
          floor_name: floorName,
          front_door: doors.front,
          rear_door: doors.rear,
          bank_floor_id: lfBankFloorId,
          lift_floor_id: extractId(lf.id),
          isBelowLobby: isBelow,
        };
      });
      const openings: FloorOpening[] = reorderBelowLobbyFirst(mapped, (o) => o.isBelowLobby).map(
        ({ isBelowLobby, ...rest }) => rest,
      );

      const belowLobby = openings.filter((o) => {
        const bankFloor = bank.bank_floors.find(
          (bf: any) =>
            (bf.floorNo ?? bf.floor_no) === o.floor_no &&
            (bf.floorBelowLobby ?? bf.floor_below_lobby),
        );
        return !!bankFloor;
      }).length;

      return {
        name: lift.name || "",
        position: lift.position ?? 1,
        car_num: lift.carNum ?? lift.car_num ?? null,
        vendor_lift_path: lift.vendorLiftPath ?? lift.vendor_lift_path ?? null,
        vendor_group_name: lift.vendorGroupName ?? lift.vendor_group_name ?? null,
        floor_count: openings.length || bank.floor_count,
        floor_below_lobby: belowLobby || bank.floor_below_lobby,
        openings:
          openings.length > 0
            ? openings
            : buildDefaultOpenings(bank.floor_count, bank.floor_below_lobby, 1, bank.bank_floors),
        saved: true,
        verified: true,
        liftId: extractId(lift.id),
      };
    });
}

/**
 * Overlay authoritative liftId/saved (and floor lift_floor_id) onto lift
 * entries restored from a draft, matched by position.
 *
 * A resume draft can be arbitrarily stale -- captured before a lift was ever
 * saved, or from a session that predates a given field being tracked at all
 * -- so it must never be trusted as the source of truth for backend-assigned
 * ids. This never overwrites a field the draft already has populated, so
 * in-progress local edits (name changes, door toggles) the draft carries are
 * preserved; it only backfills identity fields the draft is missing from a
 * fresh load of the bank's actual lifts.
 */
export function reconcileLiftIds(draftEntries: LiftEntry[], bank: BankOption): LiftEntry[] {
  const fresh = loadExistingLifts(bank);
  const freshByPosition = new Map(fresh.map((f) => [f.position, f]));
  return draftEntries.map((entry) => {
    const freshEntry = freshByPosition.get(entry.position);
    if (!freshEntry) return entry;
    const openings = entry.openings.map((o) => {
      if (o.lift_floor_id) return o;
      const freshOpening = freshEntry.openings.find((fo) => fo.bank_floor_id === o.bank_floor_id);
      return freshOpening?.lift_floor_id ? { ...o, lift_floor_id: freshOpening.lift_floor_id } : o;
    });
    return {
      ...entry,
      liftId: entry.liftId || freshEntry.liftId,
      saved: entry.saved || freshEntry.saved,
      openings,
    };
  });
}

/**
 * Reconcile bank-floor entries (restored from a draft, or already in state)
 * against the bank's current, authoritative bank_floors — matched by id. Two
 * things can go stale between when these entries were captured and now: a
 * position resequence on the Banks step (e.g. after a floor delete), and a
 * floor being deleted there entirely. Door capability is NOT tracked on
 * BankFloorEntry and so is not this function's concern — it is read live off
 * `selectedBank` instead (see `liveDoorCapability` in DERIVED STATE and
 * `canOpenDoor` above), which is always fresh regardless of this reconcile
 * pass.
 *
 * Overlays fresh position onto every entry that still exists — the same
 * rationale as reconcileLiftIds above: a resume draft can predate a position
 * resequence made on the Banks step. Position is server- and repair-managed,
 * never a direct input here (see BankFloorEntry's own doc comment), so the
 * fresh bank value always wins. Every other field (name, ...) is left
 * exactly as the entry had it, so in-progress edits survive.
 *
 * Does NOT overlay position when the fresh bank_floors' own position column
 * is degenerate (every floor sharing one value, typically 0 — see
 * hasDegeneratePositions in utils/bankFloorPositions.ts). A degenerate
 * position column is never a source of truth — it's exactly the broken state
 * assignSequentialPositions exists to repair client-side, and that repair is
 * staged (not sent to the server) until the next save. Overlaying the raw
 * degenerate value here would silently undo the repair the moment this
 * reconcile pass next runs, which — since bankFloorState is one of this
 * function's own effect's dependencies — is virtually immediately.
 *
 * DROPS any entry whose id has no match in the fresh bank at all — that
 * floor was deleted on the Banks step since this entry was captured. Every
 * BankFloorEntry id is always sourced from a real bank_floor.id the server
 * returned (this step has no add-floor flow, no client-generated ids), so an
 * absent id is unconditionally a deletion, never a floor merely not loaded
 * yet. Keeping it instead of dropping it would re-send a bank_floor id
 * LiftCloud can no longer find on the next save — updateBankFloors rejects
 * the WHOLE request for one such row, not just that row.
 *
 * Exception: never drops anything when the fresh bank has ZERO floors at
 * all. `fetchBankDetails` defaults a bank's bank_floors to [] on ANY fetch
 * error for that bank (network blip, timeout, 5xx) — indistinguishable, from
 * this function's point of view, from "every floor was actually deleted." A
 * genuinely all-floors-deleted bank is a real but rare administrative action;
 * a transient fetch failure is common. Treating an entirely-empty fresh set
 * as inconclusive rather than authoritative means a network blip degrades to
 * "keep showing the stale table, don't refresh," not "silently wipe every
 * bank_floor entry and, via liveOpeningsFor below, every lift's floor
 * openings too."
 */
export function reconcileBankFloorCapabilities(
  draftEntries: BankFloorEntry[],
  bank: BankOption,
): BankFloorEntry[] {
  const fresh = extractBankFloorOpenings({ bank_floors: bank.bank_floors });
  if (fresh.length === 0) return draftEntries;
  const freshPositionsAreDegenerate =
    fresh.length >= 2 && fresh.every((f) => f.position === fresh[0]!.position);
  const freshById = new Map(fresh.map((f) => [f.id, f]));
  let changed = false;
  const next: BankFloorEntry[] = [];
  draftEntries.forEach((entry) => {
    const freshEntry = freshById.get(entry.id);
    if (!freshEntry) {
      changed = true;
      return;
    }
    const positionMatches = freshPositionsAreDegenerate || freshEntry.position === entry.position;
    if (positionMatches) {
      next.push(entry);
      return;
    }
    changed = true;
    next.push({
      ...entry,
      ...(freshPositionsAreDegenerate ? {} : { position: freshEntry.position }),
    });
  });
  // Same array reference when nothing actually changed (the common case —
  // most re-entries into this step haven't had a door edited, a floor
  // deleted, or a position resequenced on the Banks step since) so the
  // caller's setState can bail out via React's same-reference no-op instead
  // of forcing a render of the whole step.
  return changed ? next : draftEntries;
}

/**
 * Overlay bankFloorEntries' current values onto a bank's existing bank_floors
 * rows (matched by id) via the `overlay` callback, leaving every field the
 * callback doesn't touch exactly as the existing row had it — door
 * capability included. Used to keep local `banks` state in sync with a
 * bankFloorEntries edit without a full rebuild, which would silently drop
 * whatever bankFloorEntries no longer tracks (door capability today;
 * potentially something else added to the row later). Shared by
 * saveBankFloorNames and handleSaveAll's local-cache syncs, which previously
 * hand-duplicated this exact find-by-id-and-overlay shape.
 *
 * `existing.id` is matched via `extractId`, not a bare `===`/Map-key lookup:
 * on the normal FlatBuffer load path `banks[].bank_floors[].id` is a `UuidT`
 * object (see generated `BankFloorT.id`), not the normalized string
 * `BankFloorEntry.id` always is — comparing them directly never matches
 * (object identity, not value equality), which would silently turn `overlay`
 * into a no-op for every row instead of updating anything.
 */
export function overlaySavedBankFloors<T extends { id: unknown }>(
  existingBankFloors: T[],
  savedEntries: BankFloorEntry[],
  overlay: (existing: T, saved: BankFloorEntry) => T,
): T[] {
  const savedById = new Map(savedEntries.map((e) => [e.id, e]));
  return existingBankFloors.map((existing) => {
    const saved = savedById.get(extractId(existing.id));
    return saved ? overlay(existing, saved) : existing;
  });
}

/**
 * The default main-floor choice for a set of bank-floor entries: the first
 * above-lobby floor (lobby, typically), else the first floor at all, else
 * none. Shared by populateBankFloorEntries (initial selection for a bank
 * that's never had one chosen) and resolveMainFloorId below (re-selection
 * after the previous choice was pruned) — same algorithm, two callers.
 */
function pickFallbackMainFloorId(entries: BankFloorEntry[]): string {
  const firstAbove = entries.find((e) => !e.floor_below_lobby);
  return firstAbove?.id || entries[0]?.id || "";
}

/**
 * Pick the replacement main-floor id when the previously-selected one no
 * longer exists in `entries` — i.e. reconcileBankFloorCapabilities just
 * dropped its floor.
 *
 * Returns `prevMainFloorId` unchanged whenever it is empty or still present
 * in `entries` — the common case, where nothing needs to change. A pure
 * function (not inlined into the effect that calls it) so this is testable
 * without rendering the wizard step, the same reasoning as every other
 * exported helper in this file.
 */
export function resolveMainFloorId(entries: BankFloorEntry[], prevMainFloorId: string): string {
  if (!prevMainFloorId || entries.some((e) => e.id === prevMainFloorId)) {
    return prevMainFloorId;
  }
  return pickFallbackMainFloorId(entries);
}

/**
 * Locate a specific bank-floor row within a (possibly since-mutated)
 * bankFloorEntries array, by its own id rather than trusting a captured
 * array position to still point at the same row — the bankFloorEntries
 * analogue of findOpeningIndex below.
 *
 * reconcileBankFloorCapabilities (via the same reconcile effect) can drop a
 * row between the render that painted its Name input and the moment an edit
 * to it actually lands, shifting every later row's index — the identical
 * race findOpeningIndex exists to close for the per-lift Floor Openings
 * table, one table over. Unlike FloorOpening, every BankFloorEntry.id is
 * always a real, server-sourced bank_floor id (see BankFloorEntry's own doc
 * comment — this step has no add-floor flow, no client-generated ids), so
 * there is no unlinked/synthetic case to fall back on: a single id lookup
 * always suffices.
 *
 * Returns -1 when the target row no longer exists — it was itself deleted
 * on the Banks step and dropped by reconcileBankFloorCapabilities.
 */
export function findBankFloorEntryIndex(entries: BankFloorEntry[], id: string): number {
  return entries.findIndex((e) => e.id === id);
}

/**
 * The main-floor id to use when restoring bankFloorState from a persisted
 * wizard draft (server or local-ref cache): the draft's own saved choice, if
 * it has one AND it's still valid for `entries` (a floor may have been
 * deleted since the draft was written — resolveMainFloorId's own validity
 * check handles that); the ordinary new-bank fallback otherwise.
 *
 * NOT simply `resolveMainFloorId(entries, savedMainFloorId ?? "")`:
 * resolveMainFloorId's short-circuit on an empty `prevMainFloorId` returns it
 * UNCHANGED ("nothing was ever selected"), rather than falling back — every
 * draft written before this field existed has no `mainFloorId` at all, so
 * that path would leave the restored value stuck at "" forever instead of
 * picking a sane default. Shared by both of handleBankChange's draft-restore
 * branches so the empty-vs-present distinction is made in exactly one place.
 */
export function restoreMainFloorId(
  entries: BankFloorEntry[],
  savedMainFloorId: string | undefined,
): string {
  return savedMainFloorId
    ? resolveMainFloorId(entries, savedMainFloorId)
    : pickFallbackMainFloorId(entries);
}

/**
 * Whether an opening has never been linked to any real bank_floor at all —
 * buildDefaultOpenings' no-bankFloors fallback (a new lift populated while
 * this bank's floor-detail fetch had failed) seeds openings with an empty
 * bank_floor_id, one per floor slot, none of them a reference to a DELETED
 * floor (there was never a floor to delete). Shared by liveOpeningsFor,
 * openingsVerified, and findOpeningIndex below so the definition of
 * "unlinked" — and why it's treated differently from "stale" — lives in
 * exactly one place rather than being re-derived at each call site.
 */
function isUnlinkedOpening(o: FloorOpening): boolean {
  return !o.bank_floor_id;
}

/**
 * Openings whose bank_floor_id is present in `liveBankFloorIds` — drops any
 * that reference a floor deleted on the Banks step since these openings were
 * populated. Used at save time (handleSaveAll) for both the PUT payload and
 * the persisted liftEntries entry, so the read-back verification compares
 * against the same view that was actually saved, not a stale one. The
 * lift-save analogue of reconcileBankFloorCapabilities's drop-vanished-
 * entries behavior above — same root cause (a phantom bank_floor id
 * surviving into a PUT payload LiftCloud rejects unconditionally for the
 * whole request), a different piece of state.
 *
 * Never drops anything when `liveBankFloorIds` is entirely empty — the same
 * "an empty fresh set is inconclusive, not authoritative" exception
 * reconcileBankFloorCapabilities documents above (a transient bank-floor
 * fetch failure looks identical to "this bank now has zero floors"). Without
 * this, that same network blip would silently save every lift on the bank
 * with ZERO floor openings instead of leaving them as they were.
 *
 * Takes anything with `Map`'s `.size`/`.has()` shape (a `Map` or a `Set`) so
 * the caller can pass the already-memoized `liveDoorById` directly rather
 * than building a throwaway `Set` from its keys first.
 *
 * Also preserves any unlinked opening (see isUnlinkedOpening above) —
 * treating one as stale would silently empty a brand-new, not-yet-saved
 * lift's floor table the moment the bank_floors fetch next succeeds, with no
 * way to regenerate the defaults short of leaving and re-entering the step.
 */
export function liveOpeningsFor(
  openings: FloorOpening[],
  liveBankFloorIds: { size: number; has(id: string): boolean },
): FloorOpening[] {
  if (liveBankFloorIds.size === 0) return openings;
  return openings.filter((o) => isUnlinkedOpening(o) || liveBankFloorIds.has(o.bank_floor_id));
}

/**
 * Drop every lift opening whose bank floor no longer exists, and recompute
 * the affected lift's derived floor counts.
 *
 * The reactive counterpart to the filtering handleSaveAll already does at the
 * save boundary (liveOpeningsFor, called from there with the same
 * liveBankFloorIds source below). Without this, a floor deleted on the Banks
 * step keeps showing in every lift's Floor Openings table as an interactive,
 * checkable row until the next Save silently drops it — while the Bank
 * Floors table directly above has already dropped it (via
 * reconcileBankFloorCapabilities and the reconcile effect). The two tables
 * visibly disagree about which floors exist.
 *
 * `liveBankFloorIds` — pass `liveDoorById` (DERIVED STATE above), NOT
 * bankFloorEntries or a set built from it. This is deliberate and load-
 * bearing on two counts. First, what the table shows must be exactly what a
 * save will write — handleSaveAll's liveOpeningsFor call uses liveDoorById
 * too, and using a different membership source here would reintroduce, in
 * subtler form, the exact see-one-thing/save-another disagreement this
 * function exists to remove. Second, reconcileBankFloorCapabilities (and
 * therefore bankFloorEntries) is a DROP-ONLY projection — it never
 * re-acquires a floor added on the Banks step — so pruning against it instead
 * could silently delete a lift's opening for a floor that genuinely still
 * exists. liveDoorById, by contrast, is derived fresh from selectedBank on
 * every render and can never lag selectedBankId.
 *
 * `bankFloors` is a SEPARATE argument, used only by computeFloorCounts to
 * classify a surviving opening as below- or above-lobby — liveDoorById's
 * entries don't carry floor_below_lobby. Pass the freshly-reconciled
 * bankFloorEntries (the reconcile effect's own `next`), not the possibly-
 * stale raw state. (This can, in theory, misclassify a below-lobby floor
 * whose name doesn't start with "B" if that floor is somehow in liveDoorById
 * but not yet in `next` — not reachable via this effect's own guard, which
 * keeps the two in lockstep; do not "fix" this by switching the id-membership
 * argument to `next` instead of liveDoorById, which would reintroduce the
 * worse bug described above.)
 *
 * Inherits liveOpeningsFor's "an empty live set is inconclusive, not
 * authoritative" exception, and its "an empty bank_floor_id was never a
 * reference to anything" exception, for free — both live in liveOpeningsFor
 * itself, which this function calls rather than reimplementing.
 *
 * Clears saved/verified on any entry it changes — functionally required, not
 * stylistic: verified:false re-enables the Save button (disabled while
 * allLiftsVerified), and saved:false is what makes handleSaveAll's
 * existing-lift branch (`else if (!entry.saved)`) actually PUT the corrected
 * floor set — without either flag the pruned openings could never reach the
 * server. Matches toggleOpening, which clears both for every FUNCTIONAL
 * openings change (contrast updateBankFloorName/handleAutoFill, which are
 * name-only and use the separate floorNamesModified flag instead).
 *
 * Removal only, same scope boundary as reconcileBankFloorCapabilities: a
 * floor deleted and later re-added under a new id is not auto-restored to
 * any lift's openings.
 *
 * Returns the IDENTICAL `entries` reference when nothing was pruned — same
 * contract, same reason, as reconcileBankFloorCapabilities above. The effect
 * that calls this passes its result to setLiftEntries' FUNCTIONAL updater
 * form specifically so liftEntries never needs to be one of that effect's
 * own dependencies (see the effect's own doc comment) — so an unstable
 * reference here would not be an infinite *effect* loop the way it would for
 * reconcileBankFloorCapabilities/resolveMainFloorId, whose outputs DO feed a
 * real dependency. It would still be a real, avoidable cost: a spurious
 * re-render on every firing, and an unnecessary re-run of every OTHER effect
 * that genuinely does depend on liftEntries (the validation-message effect
 * further down) even when nothing about this bank's floors actually changed.
 */
export function pruneStaleLiftOpenings(
  entries: LiftEntry[],
  liveBankFloorIds: { size: number; has(id: string): boolean },
  bankFloors: BankFloorEntry[],
): LiftEntry[] {
  let changed = false;
  const next = entries.map((entry) => {
    const liveOpenings = liveOpeningsFor(entry.openings, liveBankFloorIds);
    // liveOpeningsFor only filters, so it can only shrink the array — equal
    // length is therefore exactly equivalent to "nothing was dropped."
    if (liveOpenings.length === entry.openings.length) return entry;
    changed = true;
    const { floor_count, floor_below_lobby } = computeFloorCounts(liveOpenings, bankFloors);
    return {
      ...entry,
      openings: liveOpenings,
      floor_count,
      floor_below_lobby,
      saved: false,
      verified: false,
    };
  });
  return changed ? next : entries;
}

/**
 * Whether a saved lift's floor openings match what the read-back from the
 * server actually shows. Extracted from handleSaveAll's post-save
 * verification pass so it is testable in isolation, the same rationale as
 * every other pure helper in this file (see withCarNum's doc comment).
 *
 * Only LINKED openings (isUnlinkedOpening above) are ever compared against
 * `liftFloors` — an unlinked one is vacuously matched, filtered out before
 * either the emptiness check or the per-opening comparison, not merely
 * short-circuited inside the `.every()`. It was never sent to the server in
 * the first place: both handleSaveAll save branches already filter it out of
 * the wire payload before this function is ever reached
 * (`.filter((o) => o.bank_floor_id)` for a new lift,
 * `.filter((o) => o.lift_floor_id)` for an existing one), so no `liftFloors`
 * row could ever exist to match it against.
 *
 * Filtering to `linkedOpenings` BEFORE the `liftFloors.length > 0` check
 * (rather than only inside the `.every()`, as an earlier version of this
 * function did) matters on its own: a lift whose openings are ALL unlinked
 * sends nothing, so `liftFloors` legitimately comes back empty too — under
 * the earlier version, `liftFloors.length > 0` was false, short-circuiting
 * the whole expression to `false` before the per-opening
 * `isUnlinkedOpening` exception was ever reached, permanently failing
 * verification for exactly the not-yet-saved, synthetic-openings lift this
 * function exists to stop flagging. `liftFloors.length > 0` still guards
 * what it always did — a lift with genuinely LINKED openings but a
 * suspiciously empty read-back is still treated as unverified — just scoped
 * to the openings that actually needed sending.
 */
export function openingsVerified(
  openings: FloorOpening[],
  liftFloors: any[],
  liftName: string,
): boolean {
  const linkedOpenings = openings.filter((o) => !isUnlinkedOpening(o));
  return (
    linkedOpenings.length === 0 ||
    (liftFloors.length > 0 &&
      linkedOpenings.every((o) => {
        const lf = liftFloors.find(
          (f: any) => extractId(f.bankFloorId ?? f.bank_floor_id) === o.bank_floor_id,
        );
        if (!lf) return false;
        const expectedFd = o.front_door;
        const expectedRd = o.rear_door;
        // Shared reader, not a raw `??` chain — coupled with the same swap
        // in loadExistingLifts. Both sides of this comparison must agree on
        // how a numeric door flag coerces, or a lift that saved correctly
        // reports as unverified.
        const { front: actualFd, rear: actualRd } = readDoorFlags(lf, true, false);
        if (actualFd !== expectedFd || actualRd !== expectedRd) {
          devWarn(
            `[VERIFY] MISMATCH lift ${liftName}: expected fd=${expectedFd} rd=${expectedRd}, got fd=${actualFd} rd=${actualRd}`,
          );
        }
        return actualFd === expectedFd && actualRd === expectedRd;
      }))
  );
}

/**
 * Locate a specific opening within a (possibly since-mutated) openings array,
 * by the opening's own identity rather than trusting a captured array
 * position to still point at the same row.
 *
 * toggleOpening's checkbox `onChange` closures capture whatever index the
 * table rendered a row at. pruneStaleLiftOpenings (via the reconcile effect)
 * can now remove an EARLIER opening between the render that painted a
 * checkbox and the moment its click actually lands, shifting every later
 * opening's index down by one. A captured index does not just risk pointing
 * past the end of the (now shorter) array — it can just as easily still be
 * in bounds and land on a DIFFERENT opening than the one painted, silently
 * flipping the wrong floor's door capability instead of failing loudly.
 * Looking the row up by identity instead makes that reordering harmless.
 *
 * Linked openings (see isUnlinkedOpening) use bank_floor_id — a real
 * bank_floor's id, unique within one lift's openings by construction (one
 * opening per floor). Unlinked openings all share an empty bank_floor_id
 * (buildDefaultOpenings' no-bankFloors fallback seeds one per floor slot), so
 * floor_name is used instead — NOT floor_no, which collides: that fallback's
 * below-lobby loop numbers B-floors `1..floorBelowLobby` and its above-lobby
 * loop separately numbers from `startFloorNo` (always called with 1), so an
 * ordinary bank with at least one below-lobby floor gets both a "B1" and a
 * "1" at floor_no 1. floor_name stays unique across both ranges (the "B"
 * prefix is the only thing distinguishing them) where floor_no is not.
 * Unlinked openings are never themselves the target of a prune
 * (liveOpeningsFor always preserves them, so the row being searched for here
 * is never the one that vanished; only its position can have moved).
 *
 * Returns -1 when the target opening is no longer present at all — the row
 * the operator clicked was itself deleted on the Banks step and pruned out
 * from under the click. Exported and pure so this identity logic — the part
 * of the race fix most likely to be gotten subtly wrong — is independently
 * testable rather than only reasoned about inside a non-exported closure.
 */
export function findOpeningIndex(openings: FloorOpening[], target: FloorOpening): number {
  if (!isUnlinkedOpening(target)) {
    return openings.findIndex((o) => o.bank_floor_id === target.bank_floor_id);
  }
  return openings.findIndex((o) => isUnlinkedOpening(o) && o.floor_name === target.floor_name);
}

/**
 * Whether a specific bank floor supports opening the given door at all.
 *
 * An entry present in `floors` is authoritative — an explicit false really
 * does mean "no door here" and must gate. A floor missing from the array
 * entirely (capability not loaded yet, or a synthetic opening not yet backed
 * by a real bank_floor_id) defers to `bankWideFallback` — "does ANY floor in
 * the bank have this door" — rather than hard-locking, so an incomplete load
 * degrades to the pre-fix behavior instead of a worse one.
 *
 * Takes a minimal per-floor capability shape rather than `BankFloorEntry` —
 * capability is read live (see `liveDoorCapability` in DERIVED STATE), never
 * cached on `BankFloorEntry`, so a caller only ever has this lighter shape to
 * give it. Exported as a pure function so this is unit-testable without
 * rendering the wizard step.
 */
export function canOpenDoor(
  floors: Array<{ id: string; front_door: boolean; rear_door: boolean }>,
  bankFloorId: string,
  door: "front_door" | "rear_door",
  bankWideFallback: boolean,
): boolean {
  const entry = floors.find((e) => e.id === bankFloorId);
  if (!entry) return bankWideFallback;
  return entry[door] ?? bankWideFallback;
}

interface StepLiftsFloorsProps {
  editBankId?: string;
  standalone?: boolean;
  onSaveSuccess?: () => void;
  onCancel?: () => void;
}

export default function StepLiftsFloors({
  editBankId,
  standalone = false,
  onSaveSuccess,
  onCancel,
}: StepLiftsFloorsProps = {}) {
  const [t] = useTranslation("global");
  const { theme } = useSelector((state: RootState) => state.home);
  const {
    state: wizardState,
    setState: setWizardState,
    markStepCompleted,
    unmarkStepCompleted,
    setStepValidationMessage,
  } = useWizard();
  const access = useCurrentWizardStepAccess();

  // ************************************************ */
  // LOCAL STATE ************************************ */
  const [selectedBankId, setSelectedBankId] = useState<string>("");
  const [banks, setBanks] = useState<BankOption[]>([]);
  const [liftEntries, setLiftEntries] = useState<LiftEntry[]>([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [verificationWarning, setVerificationWarning] = useState<string | null>(null);
  /** In-flight delete, keyed by liftId (or the entry's array index for an
   *  unsaved row) — gates that one Delete button's loading spinner and
   *  disables it against a double-click while the request is out. */
  const [deletingLiftKey, setDeletingLiftKey] = useState<string | null>(null);
  // One atomic slot for "the bank-floor rows currently loaded", "which bank
  // they belong to", and "the selected main floor" — previously three
  // separately-timed pieces of state (bankFloorEntries + mainFloorId as
  // useState, plus which-bank as a useRef), hand-synced at every cross-bank
  // load with nothing enforcing the pairing. mainFloorId is included here,
  // not left as its own useState, because it is exactly as bank-scoped as
  // entries (it must reference an id inside them) — leaving it separate
  // reintroduces the identical hand-sync hazard one field over, and did so
  // for real: an earlier version of this refactor left mainFloorId split out
  // and the reconcile effect's guard against a cross-bank write landing
  // mid-flight (see that effect below) protected entries but not mainFloorId,
  // which could still be set from a stale, already-superseded bank's data.
  // See the reconcile effect further down for why the pairing has to be
  // atomic at all (a real async window in handleBankChange).
  const [bankFloorState, setBankFloorState] = useState<{
    bankId: string;
    entries: BankFloorEntry[];
    mainFloorId: string;
  }>({ bankId: "", entries: [], mainFloorId: "" });
  const [floorNamesModified, setFloorNamesModified] = useState(false);
  /**
   * True when this bank's stored floor positions were degenerate and the table
   * is showing repaired ones that have not been saved yet. Surfaced rather than
   * applied quietly: `position` decides which physical floor a command reaches.
   */
  const [positionsRepaired, setPositionsRepaired] = useState(false);

  // Offset matrix state
  const [mappedTypes, setMappedTypes] = useState<MappedMessageType[]>([]);
  const [liftOffsets, setLiftOffsets] = useState<Record<string, number>[]>([]);

  // Per-bank draft cache — preserves lift/floor edits when switching banks
  const bankDraftsRef = useRef<
    Record<
      string,
      {
        liftEntries: LiftEntry[];
        bankFloorEntries: BankFloorEntry[];
        floorNamesModified: boolean;
        liftOffsets: Record<string, number>[];
        mainFloorId: string;
      }
    >
  >({});

  // Sequence counter to discard stale responses on rapid bank switching
  const bankChangeSeqRef = useRef(0);

  // ************************************************ */
  // RTK QUERY HOOKS ******************************* */
  const [triggerGetBank] = useLazyGetBankQuery();
  const [triggerGetBanksByServer] = useLazyGetBanksByServerQuery();
  const [triggerGetChannel] = useLazyGetChannelQuery();
  const [triggerPostLift] = usePostLiftMutation();
  const [triggerPutLift] = usePutLiftMutation();
  const [triggerDeleteLift] = useDeleteLiftMutation();
  const [triggerPutBank] = usePutBankMutation();
  const [triggerGetDraft] = useLazyGetDraftQuery();
  const [triggerUpsertDraft] = useUpsertDraftMutation();
  const [triggerDeleteDraft] = useDeleteDraftMutation();

  // ************************************************ */
  // DERIVED STATE ********************************* */
  const selectedBank = banks.find((b) => b.id === selectedBankId) || null;
  // The lift form has never had a protocol-conditional field before, so this is
  // the step's only protocol lookup — and the bank's raw payload is NOT a
  // reliable place to find it. `rawData` is a decoded `Archive.Bank`, and that
  // table carries `adapter_id` but no nested `adapter`, so reading the protocol
  // off it resolved to "" for every bank and the OPC segments never rendered at
  // all. Prefer the wizard's per-bank protocol map, as the Actions and
  // Notifications steps do, and keep the nested read as the fallback for a bank
  // record that does embed its adapter.
  const protocolFromBankMap = (
    wizardState.adapterProtocolByBank?.[selectedBankId] ?? ""
  ).toUpperCase();
  const isOpcPathBank = OPC_PATH_PROTOCOLS.has(
    protocolFromBankMap || adapterProtocolName((selectedBank as any)?.rawData?.adapter),
  );
  const hasFloors = selectedBank ? FLOOR_TYPES.has(selectedBank.type.toUpperCase()) : true;

  // Door capability, read LIVE from the bank rather than cached in
  // bankFloorState. Capability is not user-editable on this step (it's set
  // on the Banks step), and unlike bankFloorState.entries' other fields (name,
  // position) there is no backend-assigned identifier to wait for — contrast
  // reconcileLiftIds above, which exists because liftId/lift_floor_id
  // literally cannot exist before a save. So there is nothing to cache and
  // reconcile here: reading live means this is automatically fresh the
  // instant `banks` refreshes (the step-entry effect further down).
  // saveBankFloorNames' payload reads capability the same live way (via
  // liveDoorById below), so there is no separate cache anywhere in this file
  // that needs to be kept in sync with it.
  //
  // Reuses the shared extractor (src/utils/bankFloorOpenings.ts) rather than
  // re-deriving the camelCase/snake_case-with-fallback coercion by hand —
  // it already gets the numeric-flag edge case right (a FlatBuffer-decoded
  // door flag can arrive as a numeric 1/0, which a bare `=== true` check
  // would silently misread as false).
  const liveDoorCapability = useMemo(
    () => extractBankFloorOpenings({ bank_floors: selectedBank?.bank_floors ?? [] }),
    [selectedBank?.bank_floors],
  );
  const liveDoorById = useMemo(
    () => new Map(liveDoorCapability.map((f) => [f.id, f])),
    [liveDoorCapability],
  );

  // "Does ANY floor in the bank have this door type" — a bank-wide summary,
  // NOT a per-floor answer. Used as the fallback for canOpen below when a
  // specific floor's own capability isn't known yet, and for the bank-level
  // Rear Doors Yes/No display and the "nothing to select" disablement on the
  // Select All buttons.
  const frontDoors = liveDoorCapability.some((f) => f.front);
  const rearDoors = liveDoorCapability.some((f) => f.rear);

  // Per-FLOOR door capability, for gating each row of the openings table —
  // see canOpenDoor's doc comment for why this must not just be frontDoors/
  // rearDoors applied uniformly to every row. Reimplements canOpenDoor's O(1)
  // logic against the memoized Map directly, rather than calling canOpenDoor
  // itself (which takes an array and would re-scan it — O(F) — on every one
  // of the several calls per row this makes in a render): this closure is
  // called from checkbox `disabled` props and bulk-toggle handlers across
  // every row of the table, so an O(1) lookup here matters in a way it does
  // not for canOpenDoor's other (test, one-off) callers.
  const canOpen = (bankFloorId: string, door: "front_door" | "rear_door"): boolean => {
    const entry = liveDoorById.get(bankFloorId);
    const bankWide = door === "front_door" ? frontDoors : rearDoors;
    if (!entry) return bankWide;
    return (door === "front_door" ? entry.front : entry.rear) ?? bankWide;
  };

  // Build per-lift validation errors using computed active floor counts
  const liftValidationErrors: string[] = liftEntries.map((le, idx) => {
    const { activeTotal } = countActiveFloors(le.openings, bankFloorState.entries);
    const errors: string[] = [];
    const nameError = validateLiftName(le.name);
    if (nameError) errors.push(nameError);
    if (hasFloors && activeTotal < 2) {
      errors.push(activeFloorsValidationMessage(le.openings, bankFloorState.entries));
    }
    if (errors.length === 0) return "";
    const label = le.name.trim() || `Lift ${idx + 1}`;
    return `${label}: ${errors.join(", ")}`;
  });

  // Floor names gate this step alongside the lift errors. Kept separate from
  // liftValidationErrors because it is a property of the BANK, not of any one
  // lift — folding it in would report the same bank-wide fault once per lift.
  const floorNameError = validateFloorNames(bankFloorState.entries);

  const allLiftsValid =
    floorNameError === null &&
    liftEntries.length > 0 &&
    liftEntries.every((le) => le.name.trim() !== "") &&
    (!hasFloors ||
      liftEntries.every(
        (le) => countActiveFloors(le.openings, bankFloorState.entries).activeTotal >= 2,
      ));

  const allLiftsSaved =
    liftEntries.length > 0 && liftEntries.every((le) => le.saved) && !floorNamesModified;
  const allLiftsVerified =
    liftEntries.length > 0 && liftEntries.every((le) => le.verified) && !floorNamesModified;

  // "Every capable opening in this set is checked" — scoped to openings
  // whose floor actually supports the door, otherwise a bank with even one
  // incapable floor could never reach "all checked" and the button label
  // would be stuck on "Select All" forever. Guarded on length > 0: .every()
  // on an empty filtered array is vacuously true, which would show
  // "Deselect All" for a bank with no capable floor at all — nothing to
  // deselect. Shared by both the all-lifts and single-lift (getOpeningsColumns)
  // checked-state derivations below.
  const allCapableChecked = (
    openings: FloorOpening[],
    door: "front_door" | "rear_door",
  ): boolean => {
    const capable = openings.filter((o) => canOpen(o.bank_floor_id, door));
    return capable.length > 0 && capable.every((o) => o[door]);
  };

  const allLiftsAllFrontChecked = allCapableChecked(
    liftEntries.flatMap((le) => le.openings),
    "front_door",
  );
  const allLiftsAllRearChecked = allCapableChecked(
    liftEntries.flatMap((le) => le.openings),
    "rear_door",
  );

  const filledNameCount = bankFloorState.entries.filter((e) => e.name?.trim()).length;

  // ************************************************ */
  // FUNCTIONS ************************************* */
  const populateBankFloorEntries = (bank: BankOption) => {
    // Decorate-sort-undecorate, and reuse the decorated floorIndex as
    // `floor_index` below instead of resolving it a second time: it's a pure
    // function of `bf`, so computing it once per row is enough for both uses.
    //
    // This is the one `floorIndexOrZero` call site whose answer PERSISTS. The
    // other two — `buildDefaultOpenings` and `loadExistingLifts` above — are
    // sort comparators, where its 0-on-absent answer is at worst a tie and
    // `Array.prototype.sort` is stable. Here it becomes
    // `BankFloorEntry.floor_index`, a required `number`. Do not change this
    // site to some other fallback without first widening that type and
    // auditing every reader of `floor_index`: the PUT body construction, the
    // post-save `setBanks` round-trip, and `assignSequentialPositions`, which
    // repairs `position` FROM this sorted order.
    const sorted = bank.bank_floors
      .map((bf: any) => ({ bf, floorIndex: floorIndexOrZero(bf) }))
      .sort((a, b) => a.floorIndex - b.floorIndex);
    const entries = sorted.map(({ bf, floorIndex }) => {
      const fno = bf.floorNo ?? bf.floor_no ?? 0;
      const isBelow = bf.floorBelowLobby ?? bf.floor_below_lobby ?? false;
      const bfId = typeof bf.id === "object" ? formatUuidT(bf.id) || "" : bf.id || "";
      return {
        id: bfId,
        floor_no: fno,
        name: bf.name || (isBelow ? `B${fno}` : `${fno}`),
        floor_below_lobby: isBelow,
        floor_index: floorIndex,
        position: bf.position ?? 0,
      };
    });
    // `position` is a floor's ordinal within its bank: the API carries it,
    // bidirectional sync hashes it, and LiftCloud maintains it as a dense
    // sequence — deleting a floor renumbers the survivors 0..n-1. Nothing
    // populates it on the create path and the importer defaulted it to 0 when
    // the source had no such column, so an imported bank arrives with every
    // floor sharing one value and nothing telling them apart. Reading that back
    // and writing it straight out again on the next save is what kept such a
    // bank in that state.
    //
    // Repaired from the PHYSICAL order (`entries` is sorted by floor_index),
    // not the display order below, which puts the deepest basement first.
    // Defaults to 0-based (`assignSequentialPositions`'s `startAt`), matching
    // what the server's own resequence-on-delete writes; see
    // `utils/bankFloorPositions.ts` for the base's rationale and how a
    // destination that addresses floors from a different base overrides it. The
    // repair is only staged here — the operator sees the numbers in the table
    // and the notice above it, and saves deliberately.
    const positioned = assignSequentialPositions(entries);
    setPositionsRepaired(hasDegeneratePositions(entries));

    // Reverse below-lobby so deepest is first (B5, B4, ..., B1)
    const reordered = reorderBelowLobbyFirst(positioned, (e) => e.floor_below_lobby);
    // Default main floor to first above-lobby floor (lobby)
    setBankFloorState({
      bankId: bank.id,
      entries: reordered,
      mainFloorId: pickFallbackMainFloorId(reordered),
    });
    setFloorNamesModified(false);
  };

  const fetchBankDetails = async () => {
    // The standalone editor (`?idBank=`) runs in a fresh WizardProvider whose
    // bank list is always empty; the bank it names is all it needs.
    if (!wizardState.serverId || (wizardState.bankIds.length === 0 && !editBankId)) return;
    setBanksLoading(true);
    const token = await GETJwtToken();

    // Fetch flat bank list via the server endpoint (always succeeds).
    const serverResponse = await triggerGetBanksByServer({
      idServer: wizardState.serverId,
      page: 1,
      limit: 9999,
      searchAndFilterString: "",
      token,
    }).unwrap();
    const flatBanks: any[] = serverResponse?.data || [];

    // For each bank, fetch lifts separately (reliable) and try the
    // detailed endpoint for bank_floors.
    const baseUrl = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
    const bankPromises = flatBanks.map(async (fb: any) => {
      const fbId = formatUuidT(fb.id) || String(fb.id ?? "");

      // Fetch lifts for this bank
      let lifts: any[] = [];
      try {
        const liftData = await fetchAndDecode(
          `${baseUrl}/lifts/?page=1&limit=9999&filter_target=bank_id&filter=${fbId}`,
          token,
        );
        // fetchAndDecode already unwraps {data:[...]} wrappers, so liftData is the array
        lifts = Array.isArray(liftData) ? liftData : liftData?.data || [];
      } catch (err: any) {
        setSaveError(`Failed to load lifts for bank — ${describeError(err)}`);
      }

      // Fetch detailed bank data for bank_floors
      let bankFloors: any[] = [];
      try {
        const d = await fetchAndDecode(`${baseUrl}/banks/${fbId}`, token);
        if (d) {
          bankFloors = d.bankFloors || d.bank_floors || [];
          if (d.lifts?.length > 0 && bankFloors.length > 0) {
            lifts = d.lifts;
          }
        }
      } catch (err: any) {
        setSaveError(`Failed to load bank floor configuration — ${describeError(err)}`);
      }

      const bankId = formatUuidT(fb.id) || String(fb.id ?? "");
      const bankType =
        typeof fb.type === "number"
          ? (LiftType[fb.type as LiftType] ?? LiftType[LiftType.ELEVATOR])
          : fb.type || LiftType[LiftType.ELEVATOR];
      // Typed rather than cast: the `as BankOption` this replaces let the
      // mapping omit start_floor_no entirely, so the summary's Start Floor No
      // rendered blank for every bank. A missing field is now a compile error.
      const option: BankOption = {
        id: bankId,
        name: String(fb.name || bankId),
        type: bankType,
        number_of_lifts: fb.numberOfLifts ?? fb.number_of_lifts ?? 1,
        floor_count: fb.floorCount ?? fb.floor_count ?? 1,
        stops: fb.stops ?? 1,
        floor_below_lobby: fb.floorBelowLobby ?? fb.floor_below_lobby ?? 0,
        // Archive.Bank's schema default, the same fallback the save path uses.
        start_floor_no: fb.startFloorNo ?? fb.start_floor_no ?? 1,
        rear_doors: fb.rearDoors ?? fb.rear_doors ?? false,
        bank_floors: bankFloors,
        lifts,
        rawData: fb,
      };
      return option;
    });
    const results = await Promise.all(bankPromises);
    setBanks(results);
    if (results.length > 0 && !selectedBankId) {
      setSelectedBankId(results[0].id);
    }
    setBanksLoading(false);
  };

  const populateLiftsFromBank = (bank: BankOption) => {
    // Load existing lifts from API (wizard resume)
    if (bank.lifts.length > 0) {
      setLiftEntries(loadExistingLifts(bank));
      return;
    }
    // Create blank entries for new lifts
    const entries: LiftEntry[] = [];
    for (let i = 0; i < bank.number_of_lifts; i += 1) {
      entries.push({
        name: "",
        position: i + 1,
        car_num: null,
        vendor_lift_path: null,
        vendor_group_name: null,
        floor_count: bank.floor_count,
        floor_below_lobby: bank.floor_below_lobby,
        openings: buildDefaultOpenings(
          bank.floor_count,
          bank.floor_below_lobby,
          1,
          bank.bank_floors,
        ),
        saved: false,
        verified: false,
        liftId: "",
      });
    }
    setLiftEntries(entries);
    setSaveSuccess(false);
    setSaveError(null);
    setVerificationWarning(null);
  };

  const handleBankChange = async (bankId: string) => {
    bankChangeSeqRef.current += 1;
    const seq = bankChangeSeqRef.current;
    const token = await GETJwtToken();

    // Save current bank's state — server draft (fire-and-forget) + local ref fallback
    if (selectedBankId) {
      const draftPayload = {
        liftEntries,
        bankFloorEntries: bankFloorState.entries,
        floorNamesModified,
        liftOffsets,
        mainFloorId: bankFloorState.mainFloorId,
      };

      bankDraftsRef.current[selectedBankId] = draftPayload;

      if (wizardState.organizationId) {
        triggerUpsertDraft({
          orgId: wizardState.organizationId,
          step: 5,
          scopeId: selectedBankId,
          scopeType: "bank",
          draftData: draftPayload,
          token,
        });
      }
    }

    setSelectedBankId(bankId);
    const bank = banks.find((b) => b.id === bankId);
    if (!bank) return;

    // Try server draft first, fall back to local ref, then bank data
    let restored = false;

    if (wizardState.organizationId) {
      try {
        const result = await triggerGetDraft({
          orgId: wizardState.organizationId,
          step: 5,
          scopeId: bankId,
          token,
        }).unwrap();

        if (bankChangeSeqRef.current !== seq) return;

        const serverDraft = result?.data?.draft_data;
        if (serverDraft?.liftEntries) {
          setLiftEntries(reconcileLiftIds(serverDraft.liftEntries, bank));
          // Repaired here too, not just on the fresh-from-API path. A draft
          // carries the positions that were stored when it was written, so a
          // bank whose floors are all at position 0 stayed that way for exactly
          // the operators most likely to be looking at it — the ones resuming a
          // draft — while the notice and the derived numbers never appeared.
          // Prune any floor deleted since the draft was written, and
          // resequence positions, onto the draft's floor rows (see
          // reconcileBankFloorCapabilities's doc comment). Door capability
          // itself is read live from `selectedBank` (see liveDoorCapability
          // in DERIVED STATE), not restored from the draft.
          const restoredEntries = reconcileBankFloorCapabilities(
            assignSequentialPositions(serverDraft.bankFloorEntries),
            bank,
          );
          // Restores the operator's actual prior main-floor pick for THIS
          // bank when the draft carries one, falling back exactly like a
          // fresh bank load when the draft predates this field entirely —
          // see restoreMainFloorId's own doc comment for why that
          // distinction matters.
          setBankFloorState({
            bankId,
            entries: restoredEntries,
            mainFloorId: restoreMainFloorId(restoredEntries, serverDraft.mainFloorId),
          });
          setPositionsRepaired(hasDegeneratePositions(serverDraft.bankFloorEntries));
          const allVerified = serverDraft.liftEntries.every((le: LiftEntry) => le.verified);
          setFloorNamesModified(allVerified ? false : serverDraft.floorNamesModified);
          setLiftOffsets(serverDraft.liftOffsets);
          restored = true;
        }
      } catch {
        // Server draft not found or API error — fall through to local cache
      }
    }

    if (bankChangeSeqRef.current !== seq) return;

    if (!restored) {
      // Fall back to local ref cache
      const localDraft = bankDraftsRef.current[bankId];
      if (localDraft) {
        setLiftEntries(reconcileLiftIds(localDraft.liftEntries, bank));
        const restoredEntries = reconcileBankFloorCapabilities(
          assignSequentialPositions(localDraft.bankFloorEntries),
          bank,
        );
        // See the server-draft branch above for the restoreMainFloorId
        // rationale.
        setBankFloorState({
          bankId,
          entries: restoredEntries,
          mainFloorId: restoreMainFloorId(restoredEntries, localDraft.mainFloorId),
        });
        setPositionsRepaired(hasDegeneratePositions(localDraft.bankFloorEntries));
        const allVerified = localDraft.liftEntries.every((le) => le.verified);
        setFloorNamesModified(allVerified ? false : localDraft.floorNamesModified);
        setLiftOffsets(localDraft.liftOffsets);
        restored = true;
      }
    }

    if (!restored) {
      // No draft anywhere — populate from bank data
      populateLiftsFromBank(bank);
      populateBankFloorEntries(bank);

      const existing = bank.rawData?.offset_mapping;
      if (Array.isArray(existing) && existing.length > 0) {
        setLiftOffsets(existing);
      } else {
        const adapterTypes = extractMappedMessageTypes(
          bank.rawData?.adapter?.cdo_template || bank.rawData?.adapter?.cdo_input,
          bank.rawData?.adapter,
        );
        const zeros: Record<string, number>[] = Array.from({ length: bank.number_of_lifts }, () => {
          const row: Record<string, number> = {};
          adapterTypes.forEach((mt) => {
            row[mt.categoryKey] = 0;
          });
          return row;
        });
        setLiftOffsets(zeros);
      }
    }

    // Always load adapter CDO for offset matrix
    const types = extractMappedMessageTypes(
      bank.rawData?.adapter?.cdo_template || bank.rawData?.adapter?.cdo_input,
      bank.rawData?.adapter,
    );
    setMappedTypes(types);

    setSaveSuccess(false);
    setSaveError(null);
    setVerificationWarning(null);
  };

  // Turning a door ON only applies to openings whose floor actually supports
  // it — leaves an incapable floor's opening untouched rather than forcing it
  // true. Turning OFF is always unconditional: disabling a door is safe (and
  // must stay possible) regardless of capability, so a floor already wrongly
  // checked from stale data can still be cleared. Shared by setAllDoor and
  // setAllDoorAllLifts below, which differ only in single-lift vs. all-lifts
  // scope.
  const applyDoorValue = (
    openings: FloorOpening[],
    door: "front_door" | "rear_door",
    value: boolean,
  ): FloorOpening[] =>
    openings.map((o) => (value && !canOpen(o.bank_floor_id, door) ? o : { ...o, [door]: value }));

  const setAllDoor = (liftIndex: number, door: "front_door" | "rear_door", value: boolean) => {
    setLiftEntries((prev) => {
      const copy = [...prev];
      const lift = { ...copy[liftIndex] };
      const newOpenings = applyDoorValue(lift.openings, door, value);
      const { floor_count, floor_below_lobby } = computeFloorCounts(
        newOpenings,
        bankFloorState.entries,
      );
      copy[liftIndex] = {
        ...lift,
        openings: newOpenings,
        floor_count,
        floor_below_lobby,
        saved: false,
        verified: false,
      };
      return copy;
    });
  };

  const setAllDoorAllLifts = (door: "front_door" | "rear_door", value: boolean) => {
    setLiftEntries((prev) =>
      prev.map((lift) => {
        const newOpenings = applyDoorValue(lift.openings, door, value);
        const { floor_count, floor_below_lobby } = computeFloorCounts(
          newOpenings,
          bankFloorState.entries,
        );
        return {
          ...lift,
          openings: newOpenings,
          floor_count,
          floor_below_lobby,
          saved: false,
          verified: false,
        };
      }),
    );
  };

  const updateLiftEntry = (index: number, updates: Partial<LiftEntry>) => {
    setLiftEntries((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], ...updates, saved: false, verified: false };
      return copy;
    });
  };

  const updateLiftName = (index: number, name: string) => {
    updateLiftEntry(index, { name });
  };

  const updateLiftVendorPath = (index: number, field: OpcVendorLiftField, value: string) => {
    // Route through updateLiftEntry rather than setLiftEntries directly. That
    // helper is what marks the row dirty (`saved: false, verified: false`), and
    // the save button is disabled for as long as every lift is still verified —
    // so an OPC segment edited through the direct path left the operator with a
    // typed value, a button reading "All Lifts Verified", and no way to persist
    // it. Every other lift editor (name, car number, openings) already goes
    // through here or sets floorNamesModified, which is why only these two
    // fields locked.
    updateLiftEntry(index, { [field]: value || null });
  };

  const updateLiftCarNum = (index: number, carNum: number | null) => {
    updateLiftEntry(index, { car_num: carNum });
  };

  const updateLiftFloorBelowLobby = (index: number, value: number) => {
    const entry = liftEntries[index];
    const oldBelowLobby = entry.floor_below_lobby;
    if (value === oldBelowLobby) return;

    // Above-lobby floors stay the same; total floors adjusts with below-lobby
    const aboveFloors = entry.floor_count - oldBelowLobby;
    const newFloorCount = aboveFloors + value;

    // Current openings: below-lobby (deepest first) then above-lobby
    const currentAboveOpenings = entry.openings.slice(oldBelowLobby);
    // bankBelowFloors is already in deepest-first order from populateBankFloorEntries
    const bankBelowFloors = bankFloorState.entries.filter((bf) => bf.floor_below_lobby);

    let newBelowOpenings: FloorOpening[];
    if (value <= oldBelowLobby) {
      // Decreasing: keep the shallowest `value` floors (end of below section)
      newBelowOpenings = entry.openings.slice(oldBelowLobby - value, oldBelowLobby);
    } else {
      // Increasing: prepend deeper floors from bank before existing below openings
      const existingBelow = entry.openings.slice(0, oldBelowLobby);
      const bankBelowCount = bankBelowFloors.length;
      // bankBelowFloors is deepest-first; take the deeper floors not already present
      const newDeepFloors = bankBelowFloors
        .slice(bankBelowCount - value, bankBelowCount - oldBelowLobby)
        .map((bf) => {
          // Seed from this floor's own LIVE capability (liveDoorById, see
          // DERIVED STATE), not a blanket true/false — same rationale as
          // buildDefaultOpenings above.
          const door = liveDoorById.get(bf.id);
          return {
            floor_no: bf.floor_no,
            floor_name: bf.name || `B${bf.floor_no}`,
            front_door: door?.front ?? true,
            rear_door: door?.rear ?? false,
            bank_floor_id: bf.id,
            lift_floor_id: "",
          };
        });
      newBelowOpenings = [...newDeepFloors, ...existingBelow];
    }

    const newOpenings = [...newBelowOpenings, ...currentAboveOpenings];
    updateLiftEntry(index, {
      floor_count: newFloorCount,
      floor_below_lobby: value,
      openings: newOpenings,
    });
  };

  const toggleOpening = (
    liftIndex: number,
    opening: FloorOpening,
    door: "front_door" | "rear_door",
  ) => {
    setLiftEntries((prev) => {
      const copy = [...prev];
      const lift = { ...copy[liftIndex] };
      const openingsCopy = [...lift.openings];
      // `opening` is the exact row the operator clicked (the table's own
      // `record`), looked up by its own identity rather than a captured
      // array position — see findOpeningIndex's own doc comment for why a
      // position can silently alias a DIFFERENT row after
      // pruneStaleLiftOpenings (via the reconcile effect) removes an earlier
      // opening between paint and click, and why -1 (the clicked row was
      // itself the one removed) must drop the click rather than proceed:
      // returning `prev` unchanged lets React bail out via Object.is: the
      // unguarded deref this replaced would instead throw during render,
      // taking the wizard subtree down and losing every unsaved edit on the
      // step.
      const floorIndex = findOpeningIndex(openingsCopy, opening);
      if (floorIndex === -1) return prev;
      const oldOpening = openingsCopy[floorIndex];
      const wasInactive = !oldOpening.front_door && !oldOpening.rear_door;

      openingsCopy[floorIndex] = {
        ...oldOpening,
        [door]: !oldOpening[door],
      };
      const newOpening = openingsCopy[floorIndex];
      const isNowInactive = !newOpening.front_door && !newOpening.rear_door;

      lift.openings = openingsCopy;

      // Determine if this is a below-lobby floor
      const bankFloor = bankFloorState.entries.find((bf) => bf.id === oldOpening.bank_floor_id);
      const isBelowLobby = bankFloor
        ? bankFloor.floor_below_lobby
        : oldOpening.floor_name.startsWith("B");

      if (!wasInactive && isNowInactive) {
        // Floor just became inactive — decrement counts
        lift.floor_count = Math.max(1, lift.floor_count - 1);
        if (isBelowLobby) {
          lift.floor_below_lobby = Math.max(0, lift.floor_below_lobby - 1);
        }
      } else if (wasInactive && !isNowInactive) {
        // Floor just became active — increment counts
        lift.floor_count += 1;
        if (isBelowLobby) {
          lift.floor_below_lobby += 1;
        }
      }

      lift.saved = false;
      lift.verified = false;
      copy[liftIndex] = lift;
      return copy;
    });
  };

  // Takes the row's own id (the render callback's `record.id`), not a
  // captured array position — the identical fix findOpeningIndex applies to
  // toggleOpening, for the identical reason: reconcileBankFloorCapabilities
  // (via the reconcile effect) can drop an EARLIER row between the render
  // that painted this Name input and the moment an edit to it lands,
  // shifting every later row's index. A stale index could silently write
  // the new name onto a DIFFERENT floor's bankFloorState.entries row and
  // sync it onto that OTHER floor's lift openings below — a wrong-floor
  // rename, not a crash. Looks the row up fresh (via findBankFloorEntryIndex,
  // inside the setState updater so it reads the true latest entries rather
  // than the possibly-stale closure value) and no-ops if it no longer
  // exists, rather than trusting floorId derived from a stale index too.
  const updateBankFloorName = (floorId: string, name: string) => {
    setBankFloorState((prev) => {
      const floorIndex = findBankFloorEntryIndex(prev.entries, floorId);
      if (floorIndex === -1) return prev;
      const copy = [...prev.entries];
      copy[floorIndex] = { ...copy[floorIndex], name };
      return { ...prev, entries: copy };
    });
    setFloorNamesModified(true);

    // Sync floor name to all lift openings referencing this bank floor
    setLiftEntries((prev) =>
      prev.map((le) => ({
        ...le,
        openings: le.openings.map((o) =>
          o.bank_floor_id === floorId ? { ...o, floor_name: name } : o,
        ),
      })),
    );
  };

  const handleAutoFill = () => {
    const names = bankFloorState.entries.map((e) => e.name ?? "");
    const derived = deriveFloorSequence(names, bankFloorState.entries.length);
    if (!derived) {
      message.warning("Enter at least 2 numeric floor names to detect a pattern");
      return;
    }
    // Build a map of floor id → new name for efficient lift opening sync
    const nameByFloorId = new Map<string, string>();
    const updatedEntries = bankFloorState.entries.map((e, i) => {
      const newName = derived[i];
      if (newName !== e.name) {
        nameByFloorId.set(e.id, newName);
      }
      return { ...e, name: newName };
    });
    setBankFloorState((prev) => ({ ...prev, entries: updatedEntries }));
    setFloorNamesModified(true);
    if (nameByFloorId.size > 0) {
      setLiftEntries((prev) =>
        prev.map((le) => ({
          ...le,
          openings: le.openings.map((o) => {
            const newName = o.bank_floor_id ? nameByFloorId.get(o.bank_floor_id) : undefined;
            return newName !== undefined ? { ...o, floor_name: newName } : o;
          }),
        })),
      );
    }
  };

  /**
   * The complete `PUT /banks/:id` body for the currently selected bank.
   *
   * `bank` supplies the header fields as this component last saw them, and
   * `numberOfLifts` the car count the save should leave behind — which is not
   * always `bank.number_of_lifts`: deleting a lift passes the decremented count.
   * Everything else is re-read from `bank.rawData`, the decoded FlatBuffer record
   * this component fetched for the bank. The door-capability flags and the floor
   * rows are read from this component's derived state, so the body is only ever
   * correct for the selected bank — both callers pass `selectedBank`.
   *
   * WHY THE WHOLE RECORD, NOT THE COLUMNS BEING CHANGED
   *
   * Two independent reasons, both measured against LiftCloud:
   *  - `UpdateBank` resolves the destination channel from the payload BEFORE it
   *    loads the bank, and answers 400 "Invalid Channel ID" when `channel_id` is
   *    absent (`resolveDestinationChannel` in bank.controller.go; the serializer
   *    maps `t.channelId` from the body and nothing else). A partial body is
   *    therefore not merely discouraged — it cannot succeed at all.
   *  - every scalar the `UpdateBank` serializer defaults when the body omits it
   *    is then written unconditionally: `active`, `front_doors` and `visible`
   *    default to true and `start_floor_no` to 1, so a body that leaves them out
   *    silently rewrites those columns even once the request is accepted.
   *
   * The JSON and binary blobs are the exception, and are deliberately not
   * carried: `applyOptionalBankUpdateFields` guards each of them
   * (`if !isEmptyBankJSON(...)`) rather than clearing on absent, so omitting
   * offset_mapping / disabled_columns / intra_content_flags /
   * socket_message_priority / interface / the OPC UA node mapping preserves what
   * is stored.
   */
  const buildBankUpdateBody = (
    bank: BankOption,
    numberOfLifts: number,
  ): Record<string, unknown> => {
    const raw = bank.rawData;
    // rawData comes from FlatBuffer decoding (camelCase keys).
    // Use BankOption fields for values already extracted, and
    // fall back through camelCase → snake_case for rawData-only fields.
    const channelId =
      formatUuidT(raw.channelId) || raw.channel_id || formatUuidT(raw.channel?.id) || "";
    const adapterId =
      formatUuidT(raw.adapterId) || raw.adapter_id || formatUuidT(raw.adapter?.id) || "";
    const orgId =
      formatUuidT(raw.orgId) ||
      raw.org_id ||
      formatUuidT(raw.organizationId) ||
      formatUuidT(raw.organization?.id) ||
      wizardState.organizationId ||
      "";
    const bankBody: Record<string, unknown> = {
      name: bank.name,
      type: raw.type,
      number_of_lifts: numberOfLifts,
      floor_count: bank.floor_count,
      floor_below_lobby: bank.floor_below_lobby,
      stops: bank.stops,
      // Same live capability the checkbox gating reads (see frontDoors/
      // rearDoors in DERIVED STATE) — one source of truth for door
      // capability, not a second one derived from bankFloorState.entries
      // that could disagree with it. Matches the rule StepBanks documents
      // for its own header switches.
      front_doors: frontDoors,
      rear_doors: rearDoors,
      // Carried because the serializer defaults it to true when the body omits
      // it, and a bank saved with the visibility flag off must stay off.
      visible: raw.visible ?? true,
      channel_id: channelId,
      adapter_id: adapterId,
      org_id: orgId,
      active: raw.active ?? true,
      interface: raw.interfaceJson || raw.interface || {},
      start_floor_no: raw.startFloorNo ?? raw.start_floor_no ?? 1,
      main_floor_id: bankFloorState.mainFloorId || undefined,
      // The floor rows come from this step's floor editor, which is this bank's
      // — the same state the draft payload beside the delete below is written
      // from. Sending an empty list is harmless rather than destructive:
      // `updateBankFloors` iterates the rows it is given and deletes nothing, so
      // a bank whose editor has no rows (an escalator, a bank whose floors were
      // never configured) keeps every floor it has.
      bank_floors: bankFloorState.entries.map((bf) => {
        // Read live from `selectedBank` (via liveDoorById, see DERIVED
        // STATE) rather than a cached bankFloorState.entries field —
        // capability isn't user-editable on this step, so the bank's own
        // current value is always what this save should re-send. `?? false`
        // only for a floor liveDoorById has no entry for at all.
        const door = liveDoorById.get(bf.id);
        return {
          id: bf.id,
          name: bf.name,
          floor_no: bf.floor_no,
          floor_index: bf.floor_index,
          position: bf.position,
          floor_below_lobby: bf.floor_below_lobby,
          front_door: door?.front ?? false,
          rear_door: door?.rear ?? false,
        };
      }),
    };
    const logicalPort = raw.logicalPort ?? raw.logical_port;
    if (logicalPort != null) {
      bankBody.logical_port = logicalPort;
    }
    return withEventReportingAddress(
      bankBody,
      raw.eventReportingAddress ?? raw.event_reporting_address ?? null,
    );
  };

  const saveBankFloorNames = async (token: string) => {
    if (!floorNamesModified || !selectedBank) return;
    const bankBody = buildBankUpdateBody(selectedBank, selectedBank.number_of_lifts);
    await triggerPutBank({
      id: selectedBankId,
      body: bankBody,
      token,
    }).unwrap();
    setFloorNamesModified(false);

    // Update local banks state with new floor names
    setBanks((prev) =>
      prev.map((b) => {
        if (b.id !== selectedBankId) return b;
        return {
          ...b,
          bank_floors: overlaySavedBankFloors(
            b.bank_floors,
            bankFloorState.entries,
            (bf, updated) => ({
              ...bf,
              name: updated.name,
            }),
          ),
        };
      }),
    );
  };

  /**
   * Shared lift POST/PUT body fields. `car_num` is an optional passthrough
   * (mirrors Bank.logical_port, `StepBanks.tsx:1136/1390`) — only included
   * when set, so unconfigured lifts don't send a stray `car_num: null`.
   *
   * The OPC segments are authoritative exactly when this step renders their
   * inputs (`isOpcPathBank`), which is what lets a cleared input delete a stored
   * path. See `withVendorPaths`.
   */
  const buildLiftBody = (entry: LiftEntry): Record<string, unknown> =>
    withVendorPaths(
      withCarNum(
        {
          name: entry.name,
          position: entry.position,
          type: toLiftType(selectedBank!.type),
          bankId: selectedBankId,
          organizationId: wizardState.organizationId,
        },
        entry.car_num,
      ),
      entry,
      isOpcPathBank,
    );

  const handleSaveAll = async () => {
    if (!selectedBank || !allLiftsValid) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    setVerificationWarning(null);
    const token = await GETJwtToken();
    try {
      // Save bank floor names if modified
      await saveBankFloorNames(token);

      const updatedEntries = [...liftEntries];

      // Floors deleted on the Banks step since these openings were populated
      // must stop riding into a lift save too — the same phantom-reference
      // class of bug bankFloorEntries has above, reachable independently of
      // it (saving a LIFT, not a floor rename, hits the same LiftCloud
      // per-row-existence rejection through PUT /lifts/{id} instead of PUT
      // /banks/{id}). Filtered once per lift below and persisted back into
      // updatedEntries[i].openings so the read-back verification further
      // down compares against the same pruned view that was actually saved,
      // not the stale one.
      //
      // Reuses liveDoorById (DERIVED STATE above) directly rather than
      // re-parsing selectedBank.bank_floors or copying it into a throwaway
      // Set — it's already the memoized id map for this exact bank_floors
      // array, keyed the same way, and liveOpeningsFor accepts anything with
      // Map's .size/.has() shape.
      for (let i = 0; i < updatedEntries.length; i += 1) {
        const entry = updatedEntries[i];
        const liveOpenings = liveOpeningsFor(entry.openings, liveDoorById);

        if (!entry.liftId) {
          // NEW LIFT: POST to create + PUT to update floors
          const postBody = buildLiftBody(entry);
          // eslint-disable-next-line no-await-in-loop
          const postResult = await triggerPostLift({
            body: postBody,
            token,
          }).unwrap();

          const liftData = postResult.data || postResult;
          const liftId = formatUuidT(liftData.id);
          // POST response may use camelCase (FlatBuffer) or snake_case (JSON)
          const liftFloors: any[] = liftData.liftFloors ?? liftData.lift_floors ?? [];

          // Build a map of bank_floor_id → lift_floor_id from POST response (may be empty
          // when the server does not auto-create floor associations on lift creation).
          const floorIdByBankFloor = new Map<string, string>();
          liftFloors.forEach((lf: any) => {
            const bfId = extractId(lf.bankFloorId ?? lf.bank_floor_id);
            if (bfId) floorIdByBankFloor.set(bfId, extractId(lf.id));
          });

          // Always save floor openings that have a bank_floor_id configured.
          // When the POST returned floors, use their IDs; otherwise omit the id
          // so the server creates the associations.
          const floors = liveOpenings
            .filter((o) => o.bank_floor_id)
            .map((o) => {
              const lfId = floorIdByBankFloor.get(o.bank_floor_id);
              return {
                ...(lfId ? { id: lfId } : {}),
                lift_id: liftId,
                bank_floor_id: o.bank_floor_id,
                // Written verbatim — capability is enforced at the edit
                // surface (checkbox disabled state, setAllDoor/
                // setAllDoorAllLifts, buildDefaultOpenings), not re-gated
                // here. Gating again at save time with the bank-wide rearDoors
                // flag independently produced a permanent lockout when that
                // flag was false, and disagreed with the read-back
                // verification below (which compares against o.rear_door,
                // not against a gated value), producing spurious
                // "could not be verified" warnings.
                front_door: o.front_door,
                rear_door: o.rear_door,
              };
            });

          if (floors.length > 0) {
            // eslint-disable-next-line no-await-in-loop
            await triggerPutLift({
              id: liftId,
              body: { ...buildLiftBody(entry), floors },
              token,
            }).unwrap();
          }

          updatedEntries[i] = { ...entry, openings: liveOpenings, saved: true, liftId };
        } else if (!entry.saved) {
          // EXISTING LIFT MODIFIED: PUT to update
          const floors = liveOpenings
            .filter((o) => o.lift_floor_id)
            .map((o) => ({
              id: o.lift_floor_id,
              lift_id: entry.liftId,
              bank_floor_id: o.bank_floor_id,
              // See the new-lift branch above — written verbatim, not
              // re-gated by the bank-wide rearDoors flag.
              front_door: o.front_door,
              rear_door: o.rear_door,
            }));
          try {
            // eslint-disable-next-line no-await-in-loop
            await triggerPutLift({
              id: entry.liftId,
              body: { ...buildLiftBody(entry), floors },
              token,
            }).unwrap();
            // eslint-disable-next-line no-console
            console.log(`[SAVE] PUT lift ${entry.name} SUCCESS`);
          } catch (putErr) {
            // eslint-disable-next-line no-console
            console.error(`[SAVE] PUT lift ${entry.name} FAILED:`, putErr);
            throw putErr;
          }

          updatedEntries[i] = { ...entry, openings: liveOpenings, saved: true };
        }
      }

      // Save offset_mapping to bank if matrix has data
      if (mappedTypes.length > 0 && liftOffsets.length > 0) {
        // eslint-disable-next-line no-await-in-loop
        await triggerPutBank({
          id: selectedBankId,
          body: { offset_mapping: liftOffsets },
          token,
        }).unwrap();
      }

      // Read-back verification: fetch each saved lift from the API and confirm
      // the name, position, and floor openings match what we sent.
      const baseUrl = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
      const verifiedEntries = await Promise.all(
        updatedEntries.map(async (entry) => {
          if (!entry.liftId) return { ...entry, verified: false };
          try {
            const readback = await fetchAndDecode(`${baseUrl}/lifts/${entry.liftId}`, token);
            const lift = readback?.data || readback;
            const nameMatch = lift.name === entry.name;
            const posMatch = (lift.position ?? 0) === entry.position;
            const liftFloors: any[] = lift.liftFloors ?? lift.lift_floors ?? [];
            const openingsMatch = openingsVerified(entry.openings, liftFloors, entry.name);
            return { ...entry, verified: nameMatch && posMatch && openingsMatch };
          } catch {
            return { ...entry, verified: false };
          }
        }),
      );

      setLiftEntries(verifiedEntries);
      setFloorNamesModified(false);
      setSaveSuccess(true);

      const unverifiedCount = verifiedEntries.filter((e) => e.liftId && !e.verified).length;
      if (unverifiedCount > 0) {
        setVerificationWarning(
          `${unverifiedCount} lift opening${
            unverifiedCount > 1 ? "s" : ""
          } could not be verified — door settings may not have saved correctly. Edit and re-save to confirm.`,
        );
      }

      // Update local draft cache (fallback) and delete the server draft
      // since the data is now committed to production tables.
      bankDraftsRef.current[selectedBankId] = {
        liftEntries: verifiedEntries,
        bankFloorEntries: bankFloorState.entries,
        floorNamesModified: false,
        liftOffsets,
        mainFloorId: bankFloorState.mainFloorId,
      };

      if (wizardState.organizationId) {
        triggerDeleteDraft({
          orgId: wizardState.organizationId,
          step: 5,
          scopeId: selectedBankId,
          token,
        });
      }

      // Update the local banks array so switching banks and back preserves saved data.
      // Build lift objects that loadExistingLifts can parse on bank change.
      setBanks((prev) =>
        prev.map((b) => {
          if (b.id !== selectedBankId) return b;
          const savedLifts = updatedEntries.map((e) => ({
            id: e.liftId,
            name: e.name,
            position: e.position,
            carNum: e.car_num,
            liftFloors: e.openings.map((o) => ({
              id: o.lift_floor_id,
              bankFloorId: o.bank_floor_id,
              frontDoor: o.front_door,
              rearDoor: o.rear_door,
            })),
          }));
          return {
            ...b,
            lifts: savedLifts,
            // Preserve each existing bank_floors entry (door capability
            // included) and overlay only what this save actually changes —
            // see overlaySavedBankFloors' doc comment. A full rebuild from
            // bankFloorState.entries would drop door capability here, since
            // capability is read live from selectedBank and is no longer
            // tracked on bankFloorState.entries at all.
            bank_floors: overlaySavedBankFloors(
              b.bank_floors,
              bankFloorState.entries,
              (bf, updated) => ({
                ...bf,
                name: updated.name,
                floorNo: updated.floor_no,
                floorIndex: updated.floor_index,
                floorBelowLobby: updated.floor_below_lobby,
              }),
            ),
          };
        }),
      );

      // Update wizard state
      const allLiftIds = updatedEntries.map((e) => e.liftId).filter(Boolean);
      const liftNames: Record<string, string> = {};
      updatedEntries.forEach((e) => {
        if (e.liftId) {
          liftNames[e.liftId] = e.name || e.liftId;
        }
      });
      setWizardState((prev) => {
        const updated = structuredClone(prev);
        updated.liftIdsByBank[selectedBankId] = allLiftIds;
        updated.liftNamesByBank[selectedBankId] = liftNames;
        return updated;
      });
      markStepCompleted(WizardStep.Lifts);
      if (standalone && onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (err: any) {
      setSaveError(`Failed to save lifts — ${describeError(err)}`);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Deletes one lift: DELETE /lifts/{id} when it's already persisted (an
   * unsaved blank row — no liftId yet — just drops locally, nothing to call
   * the server for), then keeps every other place this lift's presence is
   * cached in sync, so the delete is actually clean rather than just
   * removing the visible row:
   *
   * - `banks` (this step's own in-memory cache): loadExistingLifts reads
   *   `bank.lifts` on the next bank switch, so leaving the deleted lift in
   *   there would resurrect it the moment the operator switches away and
   *   back. Its declared `number_of_lifts` is decremented here too.
   * - Both draft layers (bankDraftsRef's local ref and the server wizard
   *   draft). This is the same shape handleBankChange/handleSaveAll already
   *   write — necessary because reconcileLiftIds (see its own doc comment)
   *   only reconciles entries a draft ALREADY has; it never drops one that
   *   no longer exists server-side. Without this, a stale draft saved
   *   before this delete would resurrect the lift as a "ghost" entry the
   *   next time this bank is opened.
   * - The bank's own declared `number_of_lifts` counter, via a best-effort
   *   PUT. Leaving that stale-high after a delete is the exact drift class
   *   of bug already tracked for this field (see StepBanks.tsx's
   *   existingLiftCount/LIFT-791) approached from the other direction —
   *   non-blocking because the lift is gone either way; a failure here only
   *   means the count needs a manual re-save on the Banks step.
   * - wizardState.liftIdsByBank/liftNamesByBank, which Notifications and
   *   Actions read to populate their own per-lift pickers — and those two
   *   steps are unmarked incomplete, the same downstream invalidation
   *   numberOfLifts already triggers for a bank edit (wizardInvalidation.ts),
   *   so a lift bound to either of them gets flagged for review instead of
   *   silently pointing at an id that no longer exists.
   *
   * Identifies the target by object reference, not the render-time index —
   * same reasoning as findOpeningIndex/findBankFloorEntryIndex elsewhere in
   * this file: a captured index is only ever safe when nothing else can
   * resize this exact array between paint and click.
   */
  const handleDeleteLift = async (entry: LiftEntry, deleteKey: string) => {
    if (!selectedBank) return;
    setDeletingLiftKey(deleteKey);
    setSaveError(null);
    const token = await GETJwtToken();
    try {
      if (entry.liftId) {
        await triggerDeleteLift({ id: entry.liftId, token }).unwrap();
      }

      const remaining = liftEntries.filter((e) => e !== entry);
      setLiftEntries(remaining);

      setBanks((prev) =>
        prev.map((b) =>
          b.id === selectedBankId
            ? {
                ...b,
                lifts: b.lifts.filter((l: any) => extractId(l.id) !== entry.liftId),
                number_of_lifts: Math.max(0, b.number_of_lifts - 1),
              }
            : b,
        ),
      );

      const draftPayload = {
        liftEntries: remaining,
        bankFloorEntries: bankFloorState.entries,
        floorNamesModified,
        liftOffsets,
        mainFloorId: bankFloorState.mainFloorId,
      };
      bankDraftsRef.current[selectedBankId] = draftPayload;
      if (wizardState.organizationId) {
        triggerUpsertDraft({
          orgId: wizardState.organizationId,
          step: 5,
          scopeId: selectedBankId,
          scopeType: "bank",
          draftData: draftPayload,
          token,
        });
      }

      try {
        // The whole bank record, not just the count. A body carrying only
        // `number_of_lifts` could never succeed: UpdateBank resolves the
        // destination channel from the payload before it loads the bank and
        // answers 400 "Invalid Channel ID" without `channel_id`, so the lift
        // went away and the count stayed high — the exact failure this catch
        // used to report every single time. See `buildBankUpdateBody`.
        await triggerPutBank({
          id: selectedBankId,
          body: buildBankUpdateBody(selectedBank, Math.max(0, selectedBank.number_of_lifts - 1)),
          token,
        }).unwrap();
      } catch (countErr: any) {
        setSaveError(
          `Lift deleted, but the bank's lift count could not be updated — ${describeError(countErr)}`,
        );
      }

      if (entry.liftId) {
        setWizardState((prev) => {
          const updated = structuredClone(prev);
          updated.liftIdsByBank[selectedBankId] = (
            updated.liftIdsByBank[selectedBankId] ?? []
          ).filter((id: string) => id !== entry.liftId);
          if (updated.liftNamesByBank[selectedBankId]) {
            delete updated.liftNamesByBank[selectedBankId][entry.liftId];
          }
          return updated;
        });
        unmarkStepCompleted(WizardStep.Notifications, WizardStep.Actions);
      }
    } catch (err: any) {
      setSaveError(`Failed to delete lift — ${describeError(err)}`);
    } finally {
      setDeletingLiftKey(null);
    }
  };

  // ************************************************ */
  // USE EFFECTS *********************************** */

  // Push validation errors to the wizard context for the Next button tooltip
  useEffect(() => {
    // Only set validation messages when this step is active
    if (wizardState.currentStep === WizardStep.Lifts) {
      const errors = [floorNameError, ...liftValidationErrors].filter(Boolean);
      if (errors.length > 0) {
        setStepValidationMessage(errors.join(" | "));
      } else if (!allLiftsSaved) {
        setStepValidationMessage("Save all lifts before proceeding");
      } else {
        setStepValidationMessage("");
      }
    }

    // Mark step complete whenever all lifts are saved (regardless of active step)
    if (allLiftsSaved) {
      markStepCompleted(WizardStep.Lifts);
    }
    // floorNameError joins the deps: without it, clearing a name leaves the
    // stale "Save all lifts before proceeding" message standing and the real
    // reason Next is blocked never appears.
  }, [liftEntries, floorNamesModified, floorNameError, wizardState.currentStep, allLiftsSaved]);

  useEffect(() => {
    if (editBankId) return;
    fetchBankDetails();
  }, [wizardState.bankIds.length]);

  // Wizard steps are mounted once and kept alive with display:none rather
  // than unmounted on navigation (SetupWizardPage), so this step's `banks`
  // state does NOT pick up a Rear/Front Doors change made on the Banks step
  // just by the operator switching tabs — nothing here was listening for
  // that. bankIds.length above only changes on bank create/delete, not on an
  // edit to an existing bank's doors. Re-fetching on every transition INTO
  // this step (not on every render while already on it, and not in
  // standalone/editBankId mode, matching the effect above) closes that gap:
  // the reporter's exact workflow was Banks (toggle Rear Doors, save) → Lifts
  // & Floor (still reads the pre-edit bank). This does not touch
  // liftEntries, so in-progress lift edits on the currently selected bank
  // are unaffected. Unconditional rather than invalidation-gated on purpose:
  // this guarantee is documented flatly in book/src/wizard/wizard-lifts-floors.md
  // and should not become contingent on a rule table (utils/wizardInvalidation.ts)
  // in the same change that edits that rule table.
  useWizardStepEntry({
    step: WizardStep.Lifts,
    onEnter: fetchBankDetails,
    enabled: !editBankId,
  });

  // Keep bankFloorState.entries fresh whenever `banks` is re-fetched (the
  // effect above, or the count-keyed effect further up). NOT what makes the
  // checkbox table correct — canOpen (see DERIVED STATE) reads capability
  // live from `selectedBank`, not from bankFloorState, so that gating is
  // already always fresh regardless of this effect. This exists for two
  // narrower, separate concerns: (1) saveBankFloorNames' payload re-sends
  // bankFloorState.entries' front_door/rear_door/position verbatim when
  // saving a floor rename, and without this those fields would go stale even
  // though the checkboxes themselves stay correct; (2) a floor deleted on the
  // Banks step since this bank was last loaded must stop being sent at all —
  // see reconcileBankFloorCapabilities's doc comment. Independent of the
  // populate-from-scratch effect below, which only runs once per bank via its
  // liftEntries.length gate.
  //
  // Guarded on bankFloorState.bankId matching selectedBankId — NOT optional
  // now that reconcileBankFloorCapabilities drops unmatched entries instead
  // of leaving them alone: without this guard, the real timing gap in
  // handleBankChange (setSelectedBankId fires well before its own
  // draft-restore await resolves) would hit this effect with bankFloorState
  // still holding the PREVIOUS bank's entries and selectedBankId already the
  // new one — every id fails to match a different bank's floors, and the drop
  // behavior would wipe entries and mainFloorId instead of leaving them alone
  // the way the old capability-only-sync harmlessly did in this same window.
  // bankId+entries+mainFloorId living in one state slot (rather than two
  // separate useState calls plus a separately-timed ref, as this used to be)
  // is what makes "still holding the PREVIOUS bank's data" and
  // "selectedBankId already the new one" mutually exclusive by construction
  // for every OTHER reader of this state — this effect is the one place that
  // must still explicitly check for it, because it's the one effect that
  // runs BETWEEN the two atomic writes handleBankChange makes (the immediate
  // setSelectedBankId, and the later setBankFloorState once its await
  // resolves).
  //
  // Reads bankFloorState as a plain closure value rather than computing
  // `next`/`nextMain` inside the updater, deliberately: a nested-updater
  // version of this (compute the pruned result inside the state updater,
  // stash it in a `let`, use it to drive a subsequent second setState call)
  // was tried and empirically breaks under React 18 — the second update
  // silently drops whenever an earlier-declared effect in this component also
  // calls a synchronous local setState in the same commit flush. Reading the
  // value directly and listing it as a dependency is the version that is
  // actually reliable. The setBankFloorState call below still takes an
  // updater function, but only to preserve identity when nothing changed and
  // to re-validate the race guard against the freshest pending state (see its
  // own comment) — `next`/`nextMain` themselves are always computed outside,
  // so this is a different pattern than the one that broke.
  //
  // Updates entries AND mainFloorId together in the SAME setBankFloorState
  // call, not two separate setState calls (one plain, one guarded) — an
  // earlier version of this effect called setBankFloorState(...) for entries
  // (correctly guarded) followed by a bare setMainFloorId(...) for the main
  // floor (unguarded), which reintroduced exactly the race this whole effect
  // exists to prevent: a cross-bank write landing between this effect running
  // and its updates flushing would leave mainFloorId set from a bank other
  // than the one now selected, even though entries stayed correct. Both
  // fields living in bankFloorState is what makes one atomic update — and
  // therefore one guard — cover both.
  //
  // A separate setState (setLiftEntries, via pruneStaleLiftOpenings) is also
  // driven from this same effect, under the identical guard: liftEntries
  // belongs to the same bank as bankFloorState.entries whenever
  // bankFloorState.bankId matches selectedBankId (every call site that
  // replaces one replaces the other in the same synchronous block). Uses
  // setLiftEntries' FUNCTIONAL updater form — `(prev) => ...` — rather than
  // listing liftEntries as a real dependency, since liftEntries changes on
  // every keystroke into a lift name, every door checkbox click, every Select
  // All — none of which touch which bank floors exist — so making it a real
  // dependency would re-run this whole effect body on all of those, not just
  // on the bankFloorState/selectedBank/liveDoorById changes that can actually
  // make anything stale.
  useEffect(() => {
    // selectedBank (DERIVED STATE above) is the identical banks.find(...)
    // this effect would otherwise repeat — reused rather than re-scanned,
    // since this now re-runs on every bankFloorState change too.
    if (!selectedBank) return;
    if (bankFloorState.bankId !== selectedBankId) return;
    const next = reconcileBankFloorCapabilities(bankFloorState.entries, selectedBank);
    const nextMain = resolveMainFloorId(next, bankFloorState.mainFloorId);
    setBankFloorState((prev) => {
      // Re-validates against the freshest pending state rather than the
      // rendered closure — closes a race where a cross-bank write could land
      // between this effect running and this updater flushing.
      if (prev.bankId !== selectedBankId) return prev;
      // Identity-preserving no-op check, not an unconditional
      // {...prev, entries: next, mainFloorId: nextMain}: reconcileBankFloorCapabilities
      // (like assignSequentialPositions) deliberately returns its INPUT array
      // by reference when nothing actually changed, and resolveMainFloorId
      // does the same for its prevMainFloorId, specifically so this bailout
      // can fire instead of forcing a render on every pass. An unconditional
      // object spread would allocate a new top-level object every time
      // regardless of whether anything changed, permanently defeating that
      // (the previous plain-value state didn't have this problem — passing
      // the same reference/value back to a bare useState setter lets React's
      // own Object.is bailout apply directly).
      if (prev.entries === next && prev.mainFloorId === nextMain) return prev;
      return { ...prev, entries: next, mainFloorId: nextMain };
    });
    // Same guard, same commit, as the block above: bankFloorState.bankId
    // matching selectedBankId establishes bankFloorState.entries belongs to
    // this bank, and every call site that can change WHICH bank's rows are
    // loaded replaces liftEntries in the same synchronous block
    // (populateBankFloorEntries alongside populateLiftsFromBank;
    // handleBankChange's two draft-restore branches), so liftEntries belongs
    // to this bank too. Without the early return above, handleBankChange's
    // async await gap (setSelectedBankId fires well before its own
    // draft-restore resolves) would prune the OLD bank's openings against the
    // NEW bank's liveDoorById, and every id would fail to match: every
    // opening on every lift would be dropped.
    setLiftEntries((prev) => pruneStaleLiftOpenings(prev, liveDoorById, next));
  }, [selectedBank, selectedBankId, bankFloorState, liveDoorById]);

  useEffect(() => {
    if (!editBankId) return;
    const initEdit = async () => {
      const token = await GETJwtToken();
      const result = await triggerGetBank({ bankId: editBankId, token });
      const d = result.data?.data || result.data;
      if (!d) return;
      // Archive.Bank names its channel, not its server: resolve through it.
      const { serverId, organizationId: orgId } = await resolveStandaloneBankScope(d, (channelId) =>
        triggerGetChannel({ channelId, token }).unwrap(),
      );
      if (serverId) {
        setWizardState((prev) => ({
          ...prev,
          serverId,
          organizationId: orgId || prev.organizationId,
        }));
      }
    };
    initEdit();
  }, [editBankId]);

  useEffect(() => {
    if (!editBankId || !wizardState.serverId) return;
    fetchBankDetails();
  }, [wizardState.serverId]);

  useEffect(() => {
    if (editBankId && banks.length > 0 && !selectedBankId) {
      setSelectedBankId(editBankId);
    }
  }, [editBankId, banks]);

  useEffect(() => {
    if (selectedBankId && banks.length > 0) {
      const bank = banks.find((b) => b.id === selectedBankId);
      if (bank && liftEntries.length === 0) {
        populateLiftsFromBank(bank);
        populateBankFloorEntries(bank);
      }
    }
  }, [selectedBankId, banks]);

  // ************************************************ */
  // OPENINGS TABLE COLUMNS ************************ */
  const getOpeningsColumns = (liftIndex: number) => {
    const liftOpenings = liftEntries[liftIndex]?.openings ?? [];
    // Scoped to capable openings — see allCapableChecked's doc comment above
    // for why (an incapable floor would otherwise stick the label on
    // "Select All" forever, or falsely read "all checked" when nothing is).
    const allFrontChecked = allCapableChecked(liftOpenings, "front_door");
    const allRearChecked = allCapableChecked(liftOpenings, "rear_door");

    const cols: any[] = [
      {
        title: "Floor",
        dataIndex: "floor_name",
        key: "floor_name",
        width: 80,
        render: (name: string, record: FloorOpening) => {
          const inactive = !record.front_door && !record.rear_door;
          // Independent of checked state, deliberately — flags the row
          // whenever it's a dangling reference so the operator sees the
          // warning before checking it, not only once checking it trips
          // the count. See activeFloorsValidationMessage's doc comment for
          // why the validation message itself only names the checked
          // subset — this indicator and that message answer two different
          // questions ("is this row stale" vs. "why did the gate fail").
          const stale = resolveLiveBankFloor(record, bankFloorState.entries).stale;
          return (
            <>
              <WizardIdHoverTag value={record.lift_floor_id}>
                <span style={inactive ? { opacity: 0.4, textDecoration: "line-through" } : {}}>
                  {name} {inactive && "(inactive)"}
                </span>
              </WizardIdHoverTag>
              {stale && (
                // Deliberately a sibling of WizardIdHoverTag, not nested
                // inside its children: WizardIdHoverTag wraps its own
                // children in a second hover-triggered surface (the
                // lift_floor_id popover), and nesting this Tooltip inside
                // that would put two independent hover targets inside one
                // another, so hovering the warning icon could trigger both
                // popovers at once. Keeping them as siblings avoids that
                // ambiguity entirely.
                <Tooltip
                  title={`"${name}" no longer matches a floor on this bank — it was likely deleted on the Banks step. Unchecking and rechecking will not rebind it; check a different, live floor instead.`}
                >
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    style={{ color: "#faad14", marginLeft: 6 }}
                  />
                </Tooltip>
              )}
            </>
          );
        },
      },
      {
        title: (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span>Front Door</span>
            <Button
              size="small"
              disabled={!frontDoors}
              onClick={() => setAllDoor(liftIndex, "front_door", !allFrontChecked)}
            >
              {allFrontChecked ? "Deselect All" : "Select All"}
            </Button>
          </div>
        ),
        dataIndex: "front_door",
        key: "front_door",
        width: 120,
        render: (_: any, record: FloorOpening) => (
          <Checkbox
            checked={record.front_door}
            // Per-floor, not bank-wide: a floor without front-door capability
            // stays disabled, but only while unchecked — if it is somehow
            // already checked (stale data, or a floor's capability changed
            // after this opening was saved) the box stays clearable so the
            // operator can see and correct it, rather than being stuck with
            // an unclearable checked box.
            disabled={!canOpen(record.bank_floor_id, "front_door") && !record.front_door}
            onChange={() => toggleOpening(liftIndex, record, "front_door")}
          />
        ),
      },
    ];
    cols.push({
      title: (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span>Rear Door</span>
          <Button
            size="small"
            disabled={!rearDoors}
            onClick={() => setAllDoor(liftIndex, "rear_door", !allRearChecked)}
          >
            {allRearChecked ? "Deselect All" : "Select All"}
          </Button>
        </div>
      ),
      dataIndex: "rear_door",
      key: "rear_door",
      width: 120,
      render: (_: any, record: FloorOpening) => (
        <Checkbox
          checked={record.rear_door}
          // See the front-door column above — per-floor, always clearable.
          disabled={!canOpen(record.bank_floor_id, "rear_door") && !record.rear_door}
          onChange={() => toggleOpening(liftIndex, record, "rear_door")}
        />
      ),
    });
    return cols;
  };

  // ************************************************ */
  // FLOOR NAME TABLE COLUMNS ********************** */
  const floorNameColumns = [
    {
      title: "Floor #",
      dataIndex: "floor_no",
      key: "floor_no",
      width: 80,
      render: (_: any, __: BankFloorEntry, idx: number) => idx,
    },
    {
      // This floor's ordinal within the bank. Shown because a bank whose
      // positions are all identical has nothing distinguishing its floors —
      // a state that was previously invisible here.
      title: "Position",
      dataIndex: "position",
      key: "position",
      width: 90,
      render: (_: any, record: BankFloorEntry) => record.position,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (_: any, record: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Input
            size="small"
            value={record.name}
            onChange={(e: any) => updateBankFloorName(record.id, e.target.value)}
            style={{ width: 200 }}
          />
          <WizardIdHoverTag value={record.id}>
            <FontAwesomeIcon
              icon={faFingerprint}
              style={{ fontSize: 12, opacity: 0.6, flexShrink: 0 }}
            />
          </WizardIdHoverTag>
        </div>
      ),
    },
    {
      title: "Below Lobby",
      dataIndex: "floor_below_lobby",
      key: "floor_below_lobby",
      width: 100,
      render: (val: boolean) => (val ? <Tag color="blue">Yes</Tag> : null),
    },
    {
      title: "Main Floor",
      dataIndex: "id",
      key: "main_floor",
      width: 90,
      render: (id: string) => (
        <Radio
          checked={bankFloorState.mainFloorId === id}
          onChange={() => {
            setBankFloorState((prev) => ({ ...prev, mainFloorId: id }));
            setFloorNamesModified(true);
          }}
        />
      ),
    },
  ];

  // ************************************************ */
  // COMPONENT ************************************* */
  return (
    <Spin spinning={saving || banksLoading}>
      <div style={{ padding: "16px" }}>
        {/* BANK SELECTOR */}
        <DrawerRowDivider icon={faElevator} title="Lifts &amp; Floors" />
        <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
          <div className="container">
            <div className="row">
              <div className="col-12 mt-3">
                <div>
                  <span className="generalStyles__inputFlexRequired">*</span>
                  <span className="generalStyles__inputLabel">Select Bank</span>
                </div>
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)
                      ?.toLowerCase()
                      .includes(input.toLowerCase()) ?? false
                  }
                  style={{ width: "100%", marginTop: 4 }}
                  value={banks.length > 0 ? selectedBankId || undefined : undefined}
                  placeholder={banksLoading ? "Loading banks..." : "Select a bank"}
                  onChange={handleBankChange}
                  loading={banksLoading}
                  disabled={standalone && !!editBankId}
                  size="large"
                >
                  {[...banks]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((bk) => (
                      <Select.Option key={bk.id} value={bk.id}>
                        {bk.name}
                      </Select.Option>
                    ))}
                </Select>
              </div>
            </div>

            {selectedBank && (
              <>
                <div className="row">
                  <div className="col-12 col-md-6 mt-3">
                    <div>
                      <span className="generalStyles__inputFlexRequired">*</span>
                      <span className="generalStyles__inputLabel">{t("general.name")}</span>
                    </div>
                    <Input
                      style={{ width: "100%", marginTop: 4 }}
                      value={selectedBank.name}
                      disabled
                      size="small"
                    />
                  </div>
                  <div className="col-12 col-md-6 mt-3">
                    <div>
                      <span className="generalStyles__inputFlexRequired">*</span>
                      <span className="generalStyles__inputLabel">{t("general.type")}</span>
                    </div>
                    <Input
                      style={{ width: "100%", marginTop: 4 }}
                      value={selectedBank.type}
                      disabled
                      size="small"
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-12 col-md-4 mt-3">
                    <div>
                      <span className="generalStyles__inputFlexRequired">*</span>
                      <span className="generalStyles__inputLabel">
                        Number of {hasFloors ? "Lifts" : "Units"}
                      </span>
                    </div>
                    <InputNumber
                      style={{ width: "100%", marginTop: 4 }}
                      value={selectedBank.number_of_lifts}
                      disabled
                      size="small"
                    />
                  </div>
                  {hasFloors && (
                    <>
                      <div className="col-12 col-md-4 mt-3">
                        <div>
                          <span className="generalStyles__inputFlexRequired">*</span>
                          <span className="generalStyles__inputLabel">Floor Count</span>
                        </div>
                        <InputNumber
                          style={{ width: "100%", marginTop: 4 }}
                          value={selectedBank.floor_count}
                          disabled
                          size="small"
                        />
                      </div>
                      <div className="col-12 col-md-4 mt-3">
                        <div>
                          <span className="generalStyles__inputFlexRequired">*</span>
                          <span className="generalStyles__inputLabel">Floor Below Lobby</span>
                        </div>
                        <InputNumber
                          style={{ width: "100%", marginTop: 4 }}
                          value={selectedBank.floor_below_lobby}
                          disabled
                          size="small"
                        />
                      </div>
                    </>
                  )}
                </div>

                {hasFloors && (
                  <div className="row">
                    <div className="col-12 col-md-4 mt-3">
                      <div>
                        <span className="generalStyles__inputFlexRequired">*</span>
                        <span className="generalStyles__inputLabel">Start Floor No</span>
                      </div>
                      <InputNumber
                        style={{ width: "100%", marginTop: 4 }}
                        value={selectedBank.start_floor_no}
                        disabled
                        size="small"
                      />
                    </div>
                    <div className="col-12 col-md-4 mt-3">
                      <div>
                        <span className="generalStyles__inputFlexRequired">*</span>
                        <span className="generalStyles__inputLabel">Stops</span>
                      </div>
                      <InputNumber
                        style={{ width: "100%", marginTop: 4 }}
                        value={selectedBank.stops}
                        disabled
                        size="small"
                      />
                    </div>
                    <div className="col-12 col-md-4 mt-3">
                      <div>
                        <span className="generalStyles__inputFlexRequired">*</span>
                        <span className="generalStyles__inputLabel">Rear Doors</span>
                      </div>
                      <Input
                        style={{ width: "100%", marginTop: 4 }}
                        value={rearDoors ? "Yes" : "No"}
                        disabled
                        size="small"
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <br />
        <DrawerRowDivider icon={faElevator} title={t("general.floorNames")} />
        <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
          {/* BANK FLOOR NAMES */}
          {selectedBank && hasFloors && bankFloorState.entries.length > 0 && (
            <>
              <Collapse style={{ marginBottom: 8 }}>
                <Collapse.Panel
                  header={
                    <span
                      style={{ fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}
                    >
                      Floor Names ({bankFloorState.entries.length} floors)
                      {floorNamesModified && (
                        <Tag color="orange" style={{ marginLeft: 8 }}>
                          Modified
                        </Tag>
                      )}
                      <Tooltip title="Enter at least 2 numeric floor names, then click to auto-fill the rest following the same pattern">
                        <Button
                          size="small"
                          disabled={filledNameCount < 2}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAutoFill();
                          }}
                        >
                          Auto-fill Names
                        </Button>
                      </Tooltip>
                    </span>
                  }
                  key="floor-names"
                >
                  {positionsRepaired && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                      data-testid="floor-positions-repaired"
                      message="Floor positions were not set on this bank"
                      description={
                        "Every floor was stored at the same position, so the platform could not " +
                        "tell them apart — scheduled actions could not target floors " +
                        "individually, and per-floor commands all resolved to the same floor. " +
                        "The Position column below now numbers them 0 upward in floor order — " +
                        "the same numbering the server applies when a floor is deleted. Save " +
                        "this step to apply it."
                      }
                    />
                  )}

                  <Table
                    dataSource={bankFloorState.entries.map((bf, i) => ({
                      ...bf,
                      key: i,
                    }))}
                    columns={floorNameColumns}
                    pagination={false}
                    size="small"
                    scroll={bankFloorState.entries.length > 10 ? { y: 250 } : undefined}
                  />
                </Collapse.Panel>
              </Collapse>
            </>
          )}
        </div>
        <div>
          {/* LIFT ENTRIES */}
          {selectedBank && liftEntries.length > 0 && (
            <>
              <br />
              <DrawerRowDivider
                icon={faElevator}
                title={`Configure ${liftEntries.length} ${hasFloors ? "Lifts" : "Units"}`}
              />
              <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
                {/* Global select/deselect all — applied across every lift */}
                <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                  <Button
                    size="small"
                    disabled={!frontDoors}
                    onClick={() => setAllDoorAllLifts("front_door", !allLiftsAllFrontChecked)}
                  >
                    {allLiftsAllFrontChecked
                      ? "Deselect All Front Doors"
                      : "Select All Front Doors"}
                  </Button>
                  <Button
                    size="small"
                    disabled={!rearDoors}
                    onClick={() => setAllDoorAllLifts("rear_door", !allLiftsAllRearChecked)}
                  >
                    {allLiftsAllRearChecked ? "Deselect All Rear Doors" : "Select All Rear Doors"}
                  </Button>
                </div>
                <Collapse accordion={false} defaultActiveKey={["0"]} style={{ marginTop: 8 }}>
                  {liftEntries.map((entry, idx) => {
                    const { activeTotal, activeBelowLobby } = countActiveFloors(
                      entry.openings,
                      bankFloorState.entries,
                    );
                    // Stable per-entry key for the delete-in-flight indicator —
                    // liftId once saved, else an index-based placeholder (an
                    // unsaved row has no server identity yet, and this is only
                    // ever compared against itself within one render).
                    const deleteKey = entry.liftId || `unsaved-${idx}`;
                    return (
                      <Collapse.Panel
                        key={String(idx)}
                        header={
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 8,
                              width: "100%",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                flexWrap: "wrap",
                              }}
                            >
                              <span style={{ fontWeight: 500 }}>
                                {entry.name || `Lift ${idx + 1}`}
                              </span>
                              {hasFloors && (
                                <>
                                  <Tag>{countStops(entry.openings)} stops</Tag>
                                  <Tag>{activeTotal} floors</Tag>
                                </>
                              )}
                              {entry.saved && entry.liftId && (
                                <WizardEntityLink
                                  entityType="lift"
                                  entityId={entry.liftId}
                                  data={{
                                    id: entry.liftId,
                                    name: entry.name,
                                    position: entry.position,
                                    type: selectedBank?.type,
                                    bank_id: selectedBankId,
                                  }}
                                />
                              )}
                              {entry.verified && <Tag color="green">Verified</Tag>}
                              {entry.saved && !entry.verified && <Tag color="orange">Saved</Tag>}
                              {!entry.name.trim() && <Tag color="red">Name required</Tag>}
                              {hasFloors && activeTotal < 2 && (
                                <Tag color="red">Min 2 active floors</Tag>
                              )}
                            </div>
                            <WizardOpGate op="delete">
                              <Popconfirm
                                title={
                                  <span>
                                    Delete this lift?
                                    <br />
                                    {entry.liftId
                                      ? "This permanently removes the lift and its floor openings."
                                      : "This row hasn't been saved yet — it will just be dropped."}
                                  </span>
                                }
                                okText="Delete"
                                cancelText="Cancel"
                                okButtonProps={{ danger: true }}
                                onConfirm={(e) => {
                                  e?.stopPropagation();
                                  handleDeleteLift(entry, deleteKey);
                                }}
                                onCancel={(e) => e?.stopPropagation()}
                              >
                                <Button
                                  type="text"
                                  danger
                                  size="small"
                                  title="Delete lift"
                                  loading={deletingLiftKey === deleteKey}
                                  disabled={
                                    deletingLiftKey !== null && deletingLiftKey !== deleteKey
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  icon={<FontAwesomeIcon icon={faTrashCan} />}
                                />
                              </Popconfirm>
                            </WizardOpGate>
                          </div>
                        }
                      >
                        <WizardReadOnlyId
                          label="Lift ID"
                          value={entry.saved ? entry.liftId : undefined}
                        />
                        <div className="container">
                          {/* Lift fields row */}
                          <div className="row">
                            <div className="col-12 col-md-4 mt-2">
                              <FormInputText
                                inputValue={entry.name}
                                inputLabelName={t("general.name")}
                                inputOnChange={(val: string) => updateLiftName(idx, val)}
                                inputVariableName="name"
                                isRequired
                                disabledInput={false}
                                formularyType={FormularyType.WIZARD}
                              />
                            </div>
                            <div className="col-6 col-md-4 mt-2">
                              <div>
                                <span className="generalStyles__inputFlexRequired">*</span>
                                <span className="generalStyles__inputLabel">Position</span>
                              </div>
                              <InputNumber
                                style={{ width: "100%", marginTop: 4 }}
                                value={entry.position}
                                disabled
                                size="large"
                              />
                            </div>
                            <div className="col-6 col-md-4 mt-2">
                              <Tooltip title="Explicit per-car number, independent of Position/bank ordering (e.g. relabeled/swapped cars). Optional.">
                                <span className="generalStyles__inputLabel">Car Number</span>
                              </Tooltip>
                              <InputNumber
                                style={{ width: "100%", marginTop: 4 }}
                                min={0}
                                max={65535}
                                placeholder="Optional"
                                value={entry.car_num ?? undefined}
                                onChange={(val) => updateLiftCarNum(idx, val ?? null)}
                                size="large"
                              />
                            </div>
                            {isOpcPathBank &&
                              OPC_VENDOR_LIFT_FIELDS.map((field: OpcVendorLiftField) => (
                                <div className="col-6 col-md-4 mt-2" key={field}>
                                  {field === "vendor_group_name" ? (
                                    <Tooltip title={t("general.liftOpcGroupNameHelp")}>
                                      <span className="generalStyles__inputLabel">
                                        {OPC_VENDOR_LIFT_LABELS[field]}
                                      </span>
                                    </Tooltip>
                                  ) : (
                                    <span className="generalStyles__inputLabel">
                                      {OPC_VENDOR_LIFT_LABELS[field]}
                                    </span>
                                  )}
                                  <Input
                                    style={{ width: "100%", marginTop: 4 }}
                                    placeholder="Optional"
                                    value={entry[field] ?? ""}
                                    onChange={(e) =>
                                      updateLiftVendorPath(idx, field, e.target.value)
                                    }
                                    size="large"
                                  />
                                </div>
                              ))}
                          </div>
                          <div className="row">
                            {hasFloors && (
                              <>
                                <div className="col-6 col-md-4 mt-2">
                                  <div>
                                    <span className="generalStyles__inputFlexRequired">*</span>
                                    <span className="generalStyles__inputLabel">Floors</span>
                                  </div>
                                  <InputNumber
                                    style={{ width: "100%", marginTop: 4 }}
                                    value={activeTotal}
                                    disabled
                                    size="large"
                                  />
                                </div>
                                <div className="col-6 col-md-4 mt-2">
                                  <div>
                                    <span className="generalStyles__inputFlexRequired">*</span>
                                    <span className="generalStyles__inputLabel">Below Lobby</span>
                                  </div>
                                  <InputNumber
                                    style={{ width: "100%", marginTop: 4 }}
                                    min={0}
                                    max={Math.min(
                                      activeTotal,
                                      selectedBank?.floor_below_lobby ?? 0,
                                    )}
                                    value={activeBelowLobby}
                                    disabled={!selectedBank || selectedBank.floor_below_lobby === 0}
                                    size="large"
                                    onChange={(val) => updateLiftFloorBelowLobby(idx, val ?? 0)}
                                  />
                                </div>
                                <div className="col-6 col-md-4 mt-2">
                                  <div>
                                    <span className="generalStyles__inputFlexRequired">*</span>
                                    <span className="generalStyles__inputLabel">Stops</span>
                                  </div>
                                  <InputNumber
                                    style={{ width: "100%", marginTop: 4 }}
                                    value={countStops(entry.openings)}
                                    disabled
                                    size="large"
                                  />
                                </div>
                              </>
                            )}
                          </div>
                          {/* Floor openings */}
                          {hasFloors && (
                            <div className="row mt-3">
                              <div className="col-12">
                                <span
                                  className="generalStyles__inputLabel"
                                  style={{ fontWeight: 500 }}
                                >
                                  Floor Openings
                                </span>
                                <Table
                                  dataSource={entry.openings.map((o, i) => ({
                                    ...o,
                                    key: i,
                                  }))}
                                  columns={getOpeningsColumns(idx)}
                                  pagination={false}
                                  size="small"
                                  style={{ marginTop: 4 }}
                                  scroll={entry.openings.length > 10 ? { y: 300 } : undefined}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </Collapse.Panel>
                    );
                  })}
                </Collapse>
              </div>
              <br />
              <div className={`drawer__box__${theme}`}>
                <div className="container">
                  <div className="row">
                    <div className="generalStyles__flexCenter">
                      {/* SAVE ALL BUTTON */}
                      <div>
                        {!allLiftsValid && (
                          <div style={{ color: "#ff4d4f", fontSize: 12, textAlign: "right" }}>
                            {[floorNameError, ...liftValidationErrors]
                              .filter(Boolean)
                              .map((err) => (
                                <div key={err as string}>{err}</div>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <br />
              <div className={`drawer__box__${theme}`}>
                <div className="container">
                  <div className="row">
                    <div className="generalStyles__flexCenter">
                      <div className="generalStyles__mrFix">
                        {standalone && onCancel && (
                          <Button size="large" onClick={onCancel}>
                            Cancel
                          </Button>
                        )}
                      </div>
                      <div className="generalStyles__mrFix">
                        {(() => {
                          let label = "Save All Lifts";
                          if (allLiftsVerified) label = "All Lifts Verified";
                          else if (allLiftsSaved) label = "All Lifts Saved";
                          // Save All creates the unsaved lifts and updates the saved ones.
                          const lacksOp =
                            (!access.create && liftEntries.some((e) => !e.liftId)) ||
                            (!access.update && liftEntries.some((e) => !!e.liftId));
                          return (
                            <Button
                              type="primary"
                              onClick={handleSaveAll}
                              disabled={!allLiftsValid || allLiftsVerified || lacksOp}
                              loading={saving}
                              size="large"
                            >
                              {label}
                            </Button>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* OFFSET MATRIX (for Modbus adapters with mapped MessageContent types) */}
          {selectedBank && mappedTypes.length > 0 && liftEntries.length > 0 && (
            <BankOffsetMatrix
              lifts={liftEntries.map((le) => ({
                id: le.liftId,
                name: le.name,
              }))}
              mappedTypes={mappedTypes}
              offsets={liftOffsets}
              onOffsetsChange={setLiftOffsets}
              floorCount={selectedBank.floor_count}
              intraContent={false}
              adapterKeys={[]}
              onOverlapChange={() => {}}
            />
          )}

          {/* EXPORT BUTTON */}
          {!standalone && (
            <>
              <Divider />
              <div className={`drawer__box__${theme}`}>
                <div className="container">
                  <div className="row">
                    <div className="generalStyles__flexCenter">
                      <div>
                        {/* Backend stage 6 = Lifts. Previously hardcoded as 5
                            (pre-ChannelModifiers numbering), which downloaded the
                            wrong scope after the step shift. */}
                        <WizardExportButton stage={6} orgId={wizardState.organizationId} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        {verificationWarning && (
          <Alert
            type="warning"
            showIcon
            message="Lift openings could not be fully verified"
            description={verificationWarning}
            style={{ marginTop: 16 }}
          />
        )}
      </div>
      {saveError && (
        <GlobalAlert2
          isError
          isSuccess={false}
          requestType="POST"
          error={{ data: { message: saveError } }}
          name="Lift"
        />
      )}
      {saveSuccess && (
        <GlobalAlert2 isError={false} isSuccess requestType="POST" error={null} name="Lifts" />
      )}
    </Spin>
  );
}
